import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import CheckoutModal from './components/CheckoutModal';
import LoginModal from './components/LoginModal';
import UpgradePromptModal from './components/UpgradePromptModal';
import EmailCaptureModal from './components/EmailCaptureModal';
import UsageDashboard from './components/UsageDashboard';
import { useAdminAuth } from '../../hooks/useAdminAuth';
import { useDarkMode } from '../../hooks/useDarkMode';

// ── Types ────────────────────────────────────────────────────────────────────
interface Plan {
  name: string;
  price: string;
  annualPrice: string;
  period: string;
  annualPeriod: string;
  features: string[];
}

interface Toast {
  id: number;
  message: string;
  type: 'warning' | 'error' | 'success';
  icon: string;
}

interface Attachment {
  id: string;
  file: File;
  name: string;
  size: string;
  type: 'image' | 'video' | 'file';
  preview?: string;
}

interface RecentPrompt {
  id: string;
  text: string;
  toolName: string | null;
  toolIcon: string | null;
  toolColor: string | null;
  savedAt: number;
}

interface FavoritePrompt {
  id: string;
  text: string;
  toolName: string | null;
  toolIcon: string | null;
  toolColor: string | null;
  savedAt: number;
  label: string;
}

// ── Example prompts per tool ──────────────────────────────────────────────────
const TOOL_PROMPTS: Record<string, string[]> = {
  'Paper Writer': [
    'Write a 10-page research paper on the impact of social media on teenage mental health with APA citations...',
    'Analyze the causes and effects of climate change for a university-level essay with peer-reviewed sources...',
    'Compare and contrast the economic policies of Keynesian and Austrian schools of thought...',
    'Write a literature review on machine learning applications in healthcare diagnostics...',
  ],
  'Genius Math': [
    'Solve this integral: ∫(x² + 3x - 5)dx and show every step clearly...',
    'Explain the proof of the Pythagorean theorem with visual examples and real-world applications...',
    'Find the eigenvalues and eigenvectors of the matrix [[3,1],[1,3]] step by step...',
    'Solve the differential equation dy/dx = 2xy with initial condition y(0) = 1...',
  ],
  'Digital AI Search': [
    'Find the latest research on CRISPR gene editing breakthroughs published in 2024...',
    'Summarize the top 5 peer-reviewed studies on intermittent fasting and longevity...',
    'Research the current state of quantum computing and its commercial applications...',
    'Find credible sources on the economic impact of remote work post-pandemic...',
  ],
  'Resume Builder': [
    'Build an ATS-optimized resume for a senior software engineer with 8 years of React and Node.js experience...',
    'Create a resume for a marketing manager transitioning into product management...',
    'Write a resume for a recent MBA graduate targeting consulting roles at McKinsey or BCG...',
    'Design a resume for a data scientist with expertise in Python, TensorFlow, and SQL...',
  ],
  'Business Plan Writer': [
    'Write a 40-page business plan for a SaaS startup offering AI-powered HR tools for SMEs...',
    'Create an investor-ready business plan for a sustainable fashion brand targeting Gen Z...',
    'Develop a business plan for a food delivery app focused on healthy meal prep services...',
    'Write a franchise business plan for a premium coffee shop chain in Southeast Asia...',
  ],
  'eBook Creator': [
    'Write a 10,000-word eBook on personal finance for millennials: budgeting, investing, and debt freedom...',
    'Create a beginner\'s guide to mindfulness and meditation with practical daily exercises...',
    'Write an eBook on building a 6-figure freelance business from scratch in 12 months...',
    'Create a recipe eBook featuring 50 quick and healthy Mediterranean diet meals...',
  ],
  'Presentation Creator': [
    'Design a 20-slide pitch deck for a Series A fundraise for a fintech startup...',
    'Create a quarterly business review presentation with KPIs, charts, and insights...',
    'Build a compelling TED-style presentation on the future of artificial intelligence...',
    'Design a product launch presentation for a new mobile app targeting remote teams...',
  ],
  'Website Builder': [
    'Build a modern portfolio website for a UX designer with case studies and contact form...',
    'Create a landing page for a SaaS product with pricing, features, and testimonials...',
    'Generate a full e-commerce website for a handmade jewelry brand with product pages...',
    'Build a professional website for a law firm specializing in corporate litigation...',
  ],
  'AI Image Generator': [
    'Generate a photorealistic image of a futuristic city skyline at sunset with flying cars...',
    'Create a minimalist logo design for a wellness brand using soft earth tones...',
    'Generate a detailed fantasy landscape with a glowing forest and ancient ruins...',
    'Create a professional product mockup of a sleek smartwatch on a marble surface...',
  ],
  'Master Architect': [
    'Design a modern 3-bedroom house floor plan with open-concept living and a home office...',
    'Create an architectural concept for a sustainable eco-lodge in a tropical rainforest...',
    'Generate a floor plan for a 2,000 sq ft co-working space with private pods and lounges...',
    'Design a luxury penthouse layout with panoramic views and a rooftop terrace...',
  ],
  'AI Video': [
    'Create a 60-second cinematic marketing video for a luxury watch brand with dramatic lighting and slow-motion shots...',
    'Generate a 2-minute documentary-style video about ocean conservation with underwater footage and narration...',
    'Produce a 30-second social media reel showcasing a new fitness app with energetic transitions and upbeat music...',
    'Create a movie trailer for a sci-fi thriller set in 2150 with futuristic cityscapes and intense action sequences...',
  ],
};

const GENERIC_PROMPTS = [
  'Describe your paper, business plan, resume, image, website, or math problem...',
  'Write a research paper on quantum computing and its impact on cybersecurity...',
  'Create a business plan for a sustainable tech startup targeting enterprise clients...',
  'Build an ATS-optimized resume for a product manager with 5 years of experience...',
  'Generate a stunning image of a futuristic cityscape at golden hour...',
  'Design a modern portfolio website for a creative director with case studies...',
];

// ── Template data per tool ────────────────────────────────────────────────────
const TOOL_TEMPLATES: Record<string, { label: string; icon: string; prompt: string }[]> = {
  'Paper Writer': [
    { label: 'Research Paper', icon: 'ri-microscope-line', prompt: 'Write a 10-page research paper on the impact of artificial intelligence on modern healthcare systems, including ethical considerations, current applications, and future implications. Use APA citation format with at least 15 peer-reviewed sources.' },
    { label: 'Literature Review', icon: 'ri-book-read-line', prompt: 'Conduct a comprehensive literature review on climate change mitigation strategies over the past decade. Analyze key findings, methodologies, and gaps in current research. Include a structured bibliography.' },
    { label: 'Argumentative Essay', icon: 'ri-discuss-line', prompt: 'Write a compelling argumentative essay on whether universal basic income should be implemented globally. Present both sides of the debate with strong evidence, then defend one position with logical reasoning and data.' },
    { label: 'Case Study', icon: 'ri-file-search-line', prompt: 'Analyze the rise and fall of Blockbuster as a business case study. Cover strategic mistakes, missed opportunities, competitive landscape, and lessons learned for modern businesses facing digital disruption.' },
  ],
  'Genius Math': [
    { label: 'Step-by-Step Solve', icon: 'ri-function-line', prompt: 'Solve the following system of differential equations step by step: dy/dx = 3y - 2x, dz/dx = y + z. Show all intermediate steps, explain each transformation, and verify the solution.' },
    { label: 'Concept Explainer', icon: 'ri-lightbulb-line', prompt: 'Explain the concept of eigenvalues and eigenvectors in linear algebra. Start from the basics, build up to the formal definition, provide geometric intuition, and show 3 real-world applications with worked examples.' },
    { label: 'Calculus Problem', icon: 'ri-bar-chart-line', prompt: 'Evaluate the definite integral ∫₀^π (sin²x · cos³x) dx using the reduction formula method. Show every step clearly and verify using an alternative method.' },
    { label: 'Statistics Analysis', icon: 'ri-pie-chart-line', prompt: 'Perform a full hypothesis test for the following dataset comparing two groups. Calculate mean, standard deviation, t-statistic, p-value, and interpret the results at a 95% confidence level.' },
  ],
  'Digital AI Search': [
    { label: 'Topic Deep Dive', icon: 'ri-search-2-line', prompt: 'Research the latest breakthroughs in quantum computing as of 2024. Find peer-reviewed papers, summarize key findings, identify leading research institutions, and outline the timeline to practical commercial applications.' },
    { label: 'Competitive Analysis', icon: 'ri-spy-line', prompt: 'Conduct a comprehensive competitive analysis of the top 5 electric vehicle manufacturers globally. Compare market share, technology, pricing, range, charging infrastructure, and 2025 growth projections.' },
    { label: 'Trend Report', icon: 'ri-line-chart-line', prompt: 'Research and summarize the top 10 emerging technology trends for 2025. For each trend, provide current adoption rates, key players, investment figures, and predicted impact on industries.' },
    { label: 'Fact Check', icon: 'ri-shield-check-line', prompt: 'Fact-check the following claims about intermittent fasting and its health benefits. Find credible scientific sources, evaluate the quality of evidence, and provide a balanced, evidence-based summary.' },
  ],
  'Resume Builder': [
    { label: 'Software Engineer', icon: 'ri-code-s-slash-line', prompt: 'Build an ATS-optimized resume for a senior software engineer with 8 years of experience in React, Node.js, Python, and AWS. Include a strong summary, quantified achievements, and skills tailored for a Staff Engineer role at a FAANG company.' },
    { label: 'Career Change', icon: 'ri-exchange-line', prompt: 'Create a resume for a marketing manager with 6 years of experience transitioning into product management. Highlight transferable skills, reframe past achievements in product terms, and include a compelling career change summary.' },
    { label: 'Fresh Graduate', icon: 'ri-graduation-cap-line', prompt: 'Write a resume for a recent computer science graduate with no full-time experience but strong internships, open-source contributions, and academic projects. Target entry-level software developer roles at tech startups.' },
    { label: 'Executive CV', icon: 'ri-user-star-line', prompt: 'Design a C-suite executive CV for a Chief Marketing Officer with 20 years of experience. Include board memberships, P&L ownership, major brand transformations, and a powerful executive summary targeting Fortune 500 companies.' },
  ],
  'Business Plan Writer': [
    { label: 'SaaS Startup', icon: 'ri-cloud-line', prompt: 'Write a comprehensive 40-page investor-ready business plan for a B2B SaaS startup offering AI-powered HR automation tools for SMEs. Include executive summary, market analysis, competitive landscape, financial projections for 5 years, and go-to-market strategy.' },
    { label: 'E-commerce Brand', icon: 'ri-shopping-bag-line', prompt: 'Create a business plan for a sustainable e-commerce fashion brand targeting eco-conscious millennials. Cover brand positioning, supply chain strategy, digital marketing plan, unit economics, and Series A fundraising pitch.' },
    { label: 'Restaurant / Food', icon: 'ri-restaurant-line', prompt: 'Develop a business plan for a premium fast-casual restaurant chain specializing in plant-based cuisine. Include location strategy, menu development, staffing model, franchise potential, and 3-year financial projections.' },
    { label: 'Mobile App', icon: 'ri-smartphone-line', prompt: 'Write a business plan for a mental wellness mobile app targeting Gen Z users. Include user acquisition strategy, freemium monetization model, clinical advisory board structure, regulatory considerations, and seed funding requirements.' },
  ],
  'eBook Creator': [
    { label: 'Personal Finance', icon: 'ri-money-dollar-circle-line', prompt: 'Write a 15,000-word eBook titled "Money Mastery for Millennials" covering budgeting frameworks, debt elimination strategies, index fund investing, real estate basics, and building a 6-month emergency fund. Include actionable worksheets and real examples.' },
    { label: 'Self-Help Guide', icon: 'ri-heart-pulse-line', prompt: 'Create a comprehensive eBook on building unshakeable confidence and overcoming imposter syndrome. Include psychological research, 30-day action plan, journaling prompts, case studies, and practical daily habits backed by neuroscience.' },
    { label: 'How-To / Tutorial', icon: 'ri-tools-line', prompt: 'Write a step-by-step eBook on launching a profitable freelance business from scratch in 90 days. Cover niche selection, portfolio building, client acquisition, pricing strategy, contracts, and scaling to $10k/month.' },
    { label: 'Recipe Book', icon: 'ri-restaurant-line', prompt: 'Create a beautifully written recipe eBook featuring 60 quick, healthy Mediterranean diet meals. Include nutritional information, prep time, ingredient substitutions, meal prep tips, and a 4-week meal plan.' },
  ],
  'Presentation Creator': [
    { label: 'Investor Pitch Deck', icon: 'ri-funds-line', prompt: 'Design a compelling 20-slide Series A pitch deck for a fintech startup disrupting personal lending. Include problem/solution, market size ($TAM/$SAM/$SOM), traction metrics, department highlights, customer success stories, challenges, and Q3 strategic priorities.' },
    { label: 'Business Review', icon: 'ri-bar-chart-line', prompt: 'Create a 25-slide quarterly business review presentation with executive summary, KPI dashboard, revenue vs. target analysis, department highlights, customer success stories, challenges, and Q3 strategic priorities.' },
    { label: 'TED-Style Talk', icon: 'ri-mic-line', prompt: 'Build a 15-slide TED-style presentation on "The Future of Human-AI Collaboration in the Workplace." Include a compelling narrative arc, surprising statistics, personal stories, visual metaphors, and a powerful call to action.' },
    { label: 'Product Launch', icon: 'ri-rocket-line', prompt: 'Design a 20-slide product launch presentation for a new AI-powered project management tool. Cover the problem, product demo flow, key features, pricing tiers, launch timeline, marketing strategy, and success metrics.' },
  ],
  'Website Builder': [
    { label: 'Portfolio Site', icon: 'ri-layout-grid-line', prompt: 'Build a stunning portfolio website for a senior UX designer. Include an animated hero section, case study grid with hover effects, about page with timeline, skills visualization, testimonials carousel, and a contact form. Modern, minimal aesthetic.' },
    { label: 'SaaS Landing Page', icon: 'ri-pages-line', prompt: 'Create a high-converting SaaS landing page for a project management tool. Include animated hero with product screenshot, feature highlights with icons, social proof section, pricing table with toggle, FAQ accordion, and sticky CTA bar.' },
    { label: 'E-commerce Store', icon: 'ri-store-line', prompt: 'Generate a complete e-commerce website for a premium handmade jewelry brand. Include homepage with featured collections, product grid with filters, product detail pages, shopping cart, checkout flow, and brand story page.' },
    { label: 'Agency Website', icon: 'ri-building-line', prompt: 'Build a professional digital marketing agency website with animated hero, services grid, portfolio with case studies, team section, client logos, results statistics, blog preview, and a multi-step contact form.' },
  ],
  'AI Image Generator': [
    { label: 'Photorealistic Scene', icon: 'ri-camera-line', prompt: 'Generate a photorealistic image of a futuristic megacity skyline at golden hour, with flying vehicles, holographic advertisements, glass skyscrapers reflecting warm sunset light, and a bustling street-level market below. Ultra-detailed, cinematic quality.' },
    { label: 'Logo / Brand Mark', icon: 'ri-award-line', prompt: 'Create a minimalist, modern logo for a luxury wellness brand called "Aura." Use soft earth tones (sage green, warm beige, terracotta), clean geometric shapes, and a subtle leaf or circle motif. Suitable for packaging and digital use.' },
    { label: 'Fantasy Art', icon: 'ri-magic-line', prompt: 'Generate a breathtaking fantasy landscape featuring an ancient elven city built into a glowing crystal mountain, surrounded by floating islands, bioluminescent forests, and a twin-moon sky. Epic scale, painterly style, vibrant colors.' },
    { label: 'Product Mockup', icon: 'ri-box-line', prompt: 'Create a professional product mockup of a sleek, matte black smartwatch on a polished white marble surface with soft studio lighting, subtle shadow, and a blurred background. Clean, premium, suitable for e-commerce.' },
  ],
  'Master Architect': [
    { label: 'Modern Home', icon: 'ri-home-line', prompt: 'Design a modern 4-bedroom family home floor plan with open-concept living and dining, a chef\'s kitchen with island, home office, mudroom, 3-car garage, and a master suite with walk-in closet and spa bathroom. 3,500 sq ft, single story.' },
    { label: 'Eco Lodge', icon: 'ri-leaf-line', prompt: 'Create an architectural concept for a sustainable eco-lodge resort in a tropical rainforest. Include 12 elevated bungalows, a central open-air pavilion, infinity pool, solar power system, rainwater collection, and minimal environmental footprint.' },
    { label: 'Co-working Space', icon: 'ri-group-line', prompt: 'Generate a floor plan for a 5,000 sq ft premium co-working space with private offices, open hot-desking zones, 3 conference rooms, a podcast studio, phone booths, lounge area, barista bar, and rooftop terrace access.' },
    { label: 'Luxury Penthouse', icon: 'ri-building-line', prompt: 'Design a luxury penthouse layout spanning 6,000 sq ft across two floors. Include panoramic floor-to-ceiling windows, a double-height living room, chef\'s kitchen, wine cellar, home cinema, 4 en-suite bedrooms, and a wraparound rooftop terrace with pool.' },
  ],
  'AI Video': [
    { label: 'Marketing Video', icon: 'ri-megaphone-line', prompt: 'Create a 60-second high-energy marketing video for a premium tech startup launching a new AI productivity app. Include dynamic product shots, customer testimonials, sleek UI animations, upbeat background music, and a strong call-to-action. Style: modern, professional, cinematic. Resolution: 1080p, 16:9 aspect ratio.' },
    { label: 'Short Film', icon: 'ri-film-line', prompt: 'Generate a 2-minute dramatic short film about a lone astronaut discovering an ancient alien artifact on Mars. Include sweeping landscape shots, close-up emotional moments, atmospheric lighting, orchestral score, and a mysterious ending. Style: cinematic, sci-fi, realistic. Resolution: 4K, 16:9 aspect ratio.' },
    { label: 'Documentary', icon: 'ri-movie-line', prompt: 'Produce a 90-second documentary-style video exploring the impact of renewable energy on rural communities. Include aerial drone footage, interviews with locals, time-lapse of solar panel installations, natural soundscapes, and informative text overlays. Style: authentic, inspiring, educational. Resolution: 1080p, 16:9 aspect ratio.' },
    { label: 'Movie Trailer', icon: 'ri-film-line', prompt: 'Create an intense 30-second movie trailer for a psychological thriller about a detective hunting a serial killer in a rainy metropolis. Include fast-paced cuts, dramatic music crescendos, noir lighting, suspenseful dialogue snippets, and a cliffhanger ending. Style: dark, gritty, suspenseful. Resolution: 1080p, 16:9 aspect ratio.' },
  ],
};

const GENERIC_TEMPLATES = [
  { label: 'Research Paper', icon: 'ri-file-text-line', prompt: 'Write a comprehensive research paper on the impact of artificial intelligence on modern society, covering economic effects, ethical considerations, and future implications with proper academic citations.' },
  { label: 'Business Plan', icon: 'ri-briefcase-line', prompt: 'Create an investor-ready business plan for an innovative tech startup. Include executive summary, market analysis, competitive landscape, financial projections, and go-to-market strategy.' },
  { label: 'ATS Resume', icon: 'ri-profile-line', prompt: 'Build an ATS-optimized resume for a senior professional with 10 years of experience. Include a compelling summary, quantified achievements, and skills tailored for leadership roles.' },
  { label: 'Pitch Deck', icon: 'ri-slideshow-line', prompt: 'Design a compelling 20-slide investor pitch deck for a Series A fundraise. Include problem/solution, market size, traction, business model, team, and funding ask.' },
  { label: 'Futuristic Image', icon: 'ri-image-line', prompt: 'Generate a photorealistic image of a futuristic cityscape at golden hour with flying vehicles, holographic displays, and gleaming architecture. Ultra-detailed, cinematic quality.' },
  { label: 'Portfolio Website', icon: 'ri-global-line', prompt: 'Build a stunning portfolio website with animated hero, project case studies, skills section, testimonials, and a contact form. Modern, minimal, and fully responsive.' },
];

// ── Data ─────────────────────────────────────────────────────────────────────
const PLANS: Plan[] = [
  {
    name: 'Free Trial',
    price: '$0',
    annualPrice: '$0',
    period: '/month',
    annualPeriod: '/month',
    features: [
      '1 AI generation/month',
      'Access to 3 tools',
      'Basic templates',
      'Standard quality output',
      'Community support',
      'Watermarked exports',
    ],
  },
  {
    name: 'Pro',
    price: '$39',
    annualPrice: '$19',
    period: '/month',
    annualPeriod: '/month, billed annually',
    features: [
      'Unlimited AI generations',
      'Access to all 10 tools',
      'Premium templates',
      'High-quality output',
      'Priority support',
      'No watermarks',
      'Export to PDF, DOCX, PPTX',
      'Custom branding',
    ],
  },
  {
    name: 'Golden Pro',
    price: '$49',
    annualPrice: '$39',
    period: '/month',
    annualPeriod: '/month, billed annually',
    features: [
      'Everything in Pro',
      'Team collaboration (5 seats)',
      'API access',
      'White-label exports',
      'Advanced analytics',
      'Dedicated account manager',
      'Custom AI fine-tuning',
      'SLA guarantee',
      'Early access to new tools',
    ],
  },
];

const TOOLS = [
  { name: 'Paper Writer', desc: 'Generate academic papers, essays, and research documents with proper citations. Powered by OpenClaw Algorithm.', icon: 'ri-file-text-line', color: 'from-violet-500 to-violet-700', bg: 'bg-violet-50', path: '/paper-writer', tag: 'Academic', badge: 'Most Popular' },
  { name: 'Genius Math', desc: 'Solve complex equations, show step-by-step solutions, and explain math concepts. Powered by OpenClaw Algorithm.', icon: 'ri-calculator-line', color: 'from-emerald-500 to-teal-600', bg: 'bg-emerald-50', path: '/genius-math', tag: 'STEM', badge: null },
  { name: 'Digital AI Search', desc: 'AI-powered research engine that finds, summarizes, and cites sources instantly. Powered by OpenClaw Algorithm.', icon: 'ri-search-eye-line', color: 'from-cyan-500 to-sky-600', bg: 'bg-cyan-50', path: '/digital-ai-search', tag: 'Research', badge: null },
  { name: 'Resume Builder', desc: 'Craft ATS-optimized resumes tailored to any job description in minutes. Powered by OpenClaw Algorithm.', icon: 'ri-profile-line', color: 'from-orange-500 to-amber-600', bg: 'bg-orange-50', path: '/resume-builder', tag: 'Career', badge: 'Featured' },
  { name: 'Business Plan Writer', desc: 'Create investor-ready business plans with financial projections and market analysis. Powered by OpenClaw Algorithm.', icon: 'ri-briefcase-line', color: 'from-rose-500 to-pink-600', bg: 'bg-rose-50', path: '/business-plan-writer', tag: 'Business', badge: 'Featured' },
  { name: 'eBook Creator', desc: 'Write, format, and publish professional eBooks on any topic with AI assistance. Powered by OpenClaw Algorithm.', icon: 'ri-book-open-line', color: 'from-indigo-500 to-violet-600', bg: 'bg-indigo-50', path: '/ebook-creator', tag: 'Publishing', badge: null },
  { name: 'Presentation Creator', desc: 'Design stunning slide decks with AI-generated content and beautiful layouts. Powered by OpenClaw Algorithm.', icon: 'ri-slideshow-line', color: 'from-fuchsia-500 to-pink-600', bg: 'bg-fuchsia-50', path: '/presentation-creator', tag: 'Design', badge: null },
  { name: 'Website Builder', desc: 'Generate complete, responsive websites from a simple text description. Powered by OpenClaw Algorithm.', icon: 'ri-global-line', color: 'from-teal-500 to-emerald-600', bg: 'bg-teal-50', path: '/website-builder', tag: 'Web', badge: 'Most Popular' },
  { name: 'AI Image Generator', desc: 'Create stunning, high-resolution images from text prompts in seconds. Powered by OpenClaw Algorithm.', icon: 'ri-image-line', color: 'from-amber-500 to-orange-600', bg: 'bg-amber-50', path: '/ai-image-generator', tag: 'Creative', badge: 'Most Popular' },
  { name: 'Master Architect', desc: 'Design architectural plans, floor layouts, and 3D concepts with AI precision. Powered by OpenClaw Algorithm.', icon: 'ri-building-2-line', color: 'from-slate-600 to-gray-700', bg: 'bg-slate-50', path: '/master-architect', tag: 'Architecture', badge: null },
  { name: 'AI Video', desc: 'Create stunning AI-generated videos for marketing, film, documentary, movies, and more. Powered by OpenClaw Algorithm.', icon: 'ri-video-line', color: 'from-red-500 to-rose-600', bg: 'bg-red-50', path: '/ai-video', tag: 'Video', badge: 'New' },
];

const DRAFT_KEY = 'openclaw_draft';
const DRAFT_TOOL_KEY = 'openclaw_draft_tool';
const PROMPTS_KEY = 'openclaw_recent_prompts';
const FAVORITES_KEY = 'openclaw_favorites';
const WORDS_USED_KEY = 'openclaw_words_used';
const FREE_WORD_LIMIT = 1500;
const TOOL_GENS_KEY = 'openclaw_tool_gens';
const FIRST_USAGE_KEY = 'openclaw_first_usage';
const EMAIL_CAPTURED_KEY = 'openclaw_email_captured';
const MAX_ATTACHMENTS = 10;
const MAX_FILE_MB = 25;
const MAX_RECENT_PROMPTS = 10;

// ── Tone analysis ─────────────────────────────────────────────────────────────
const FORMAL_WORDS = ['therefore', 'furthermore', 'consequently', 'nevertheless', 'notwithstanding', 'henceforth', 'pursuant', 'aforementioned', 'hereby', 'wherein', 'whereas', 'accordingly', 'subsequently', 'respectively', 'predominantly', 'significantly', 'demonstrate', 'indicate', 'utilize', 'implement', 'facilitate', 'comprehensive', 'substantial', 'methodology', 'paradigm', 'hypothesis', 'empirical'];
const CASUAL_WORDS = ['hey', 'yeah', 'nope', 'gonna', 'wanna', 'gotta', 'kinda', 'sorta', 'stuff', 'things', 'like', 'basically', 'literally', 'totally', 'awesome', 'cool', 'great', 'nice', 'okay', 'ok', 'lol', 'btw', 'tbh', 'imo', 'omg', 'super', 'pretty', 'really', 'just', 'so', 'very', 'actually'];
const TECHNICAL_WORDS = ['algorithm', 'function', 'variable', 'parameter', 'interface', 'api', 'database', 'server', 'client', 'protocol', 'framework', 'library', 'module', 'component', 'architecture', 'infrastructure', 'deployment', 'integration', 'optimization', 'scalability', 'bandwidth', 'latency', 'encryption', 'authentication', 'authorization', 'repository', 'iteration', 'recursion', 'polymorphism', 'abstraction', 'calculus', 'derivative', 'integral', 'matrix', 'vector', 'coefficient', 'theorem', 'hypothesis', 'quantum', 'neural', 'regression'];

function analyzeTone(text: string): { tone: string; icon: string; color: string; bgColor: string; score: number } {
  if (!text.trim() || text.trim().split(/\s+/).length < 5) {
    return { tone: 'Neutral', icon: 'ri-equalizer-line', color: 'text-white/50', bgColor: 'bg-white/10', score: 0 };
  }
  const words = text.toLowerCase().split(/\s+/);
  const total = words.length;
  const formalScore = words.filter(w => FORMAL_WORDS.includes(w.replace(/[^a-z]/g, ''))).length / total;
  const casualScore = words.filter(w => CASUAL_WORDS.includes(w.replace(/[^a-z]/g, ''))).length / total;
  const technicalScore = words.filter(w => TECHNICAL_WORDS.includes(w.replace(/[^a-z]/g, ''))).length / total;
  const avgWordLen = words.reduce((s, w) => s + w.replace(/[^a-z]/g, '').length, 0) / total;
  const sentences = text.split(/[.!?]+/).filter(s => s.trim().length > 0);
  const avgSentLen = sentences.length > 0 ? total / sentences.length : total;
  const formalBoost = avgWordLen > 6 ? 0.04 : 0;
  const casualBoost = avgSentLen < 10 ? 0.03 : 0;
  const techBoost = avgWordLen > 7 ? 0.03 : 0;
  const fTotal = formalScore + formalBoost;
  const cTotal = casualScore + casualBoost;
  const tTotal = technicalScore + techBoost;
  const max = Math.max(fTotal, cTotal, tTotal);
  if (max < 0.01) return { tone: 'Neutral', icon: 'ri-equalizer-line', color: 'text-white/50', bgColor: 'bg-white/10', score: 0 };
  if (tTotal === max && tTotal > 0.02) return { tone: 'Technical', icon: 'ri-code-s-slash-line', color: 'text-cyan-300', bgColor: 'bg-cyan-500/20', score: tTotal };
  if (fTotal === max && fTotal > 0.015) return { tone: 'Formal', icon: 'ri-government-line', color: 'text-violet-300', bgColor: 'bg-violet-500/20', score: fTotal };
  if (cTotal === max && cTotal > 0.02) return { tone: 'Casual', icon: 'ri-chat-smile-2-line', color: 'text-amber-300', bgColor: 'bg-amber-500/20', score: cTotal };
  return { tone: 'Neutral', icon: 'ri-equalizer-line', color: 'text-white/50', bgColor: 'bg-white/10', score: 0 };
}

// ── Tips per tool ─────────────────────────────────────────────────────────────
const TOOL_TIPS: Record<string, { icon: string; tip: string }[]> = {
  'Paper Writer': [
    { icon: 'ri-bookmark-line', tip: 'Specify the citation style (APA, MLA, Chicago) upfront — it shapes the entire paper structure.' },
    { icon: 'ri-search-line', tip: 'Mention the academic level (high school, undergraduate, PhD) so the language and depth match.' },
    { icon: 'ri-list-check-2', tip: 'Include a word count target and the number of sources you need for a more precise output.' },
    { icon: 'ri-lightbulb-line', tip: 'State your thesis or argument clearly in the prompt — vague prompts produce vague papers.' },
  ],
  'Genius Math': [
    { icon: 'ri-function-line', tip: 'Use standard math notation (e.g. ∫, Σ, √) or write it out clearly — the AI understands both.' },
    { icon: 'ri-footprint-line', tip: 'Ask for "step-by-step" solutions explicitly to get fully worked-out explanations.' },
    { icon: 'ri-eye-line', tip: 'Request real-world examples or geometric intuition to make abstract concepts click.' },
    { icon: 'ri-check-double-line', tip: 'Ask the AI to verify the answer using an alternative method for extra confidence.' },
  ],
  'Digital AI Search': [
    { icon: 'ri-calendar-line', tip: 'Add a date range (e.g. "published after 2022") to get the most current research.' },
    { icon: 'ri-shield-check-line', tip: 'Ask for peer-reviewed or credible sources only to ensure research quality.' },
    { icon: 'ri-file-list-3-line', tip: 'Request a structured summary with key findings, methodology, and limitations.' },
    { icon: 'ri-global-line', tip: 'Specify the industry or region for more targeted and relevant search results.' },
  ],
  'Resume Builder': [
    { icon: 'ri-robot-line', tip: 'Mention the target job title and company type — ATS systems scan for exact keyword matches.' },
    { icon: 'ri-bar-chart-line', tip: 'Always quantify achievements: "increased sales by 40%" beats "improved sales performance".' },
    { icon: 'ri-profile-line', tip: 'Include your years of experience and top 3–5 skills to get a properly tailored summary.' },
    { icon: 'ri-briefcase-line', tip: 'Specify if you\'re changing careers — the AI will reframe your experience accordingly.' },
  ],
  'Business Plan Writer': [
    { icon: 'ri-focus-line', tip: 'Define your target market size (TAM/SAM/SOM) in the prompt for investor-ready financials.' },
    { icon: 'ri-money-dollar-circle-line', tip: 'Specify your funding stage (pre-seed, seed, Series A) to calibrate the tone and detail.' },
    { icon: 'ri-map-pin-line', tip: 'Include your geographic market and industry vertical for accurate competitive analysis.' },
    { icon: 'ri-time-line', tip: 'Ask for a 3–5 year financial projection with monthly breakdowns for the first year.' },
  ],
  'eBook Creator': [
    { icon: 'ri-user-line', tip: 'Define your target reader persona — their age, goals, and pain points shape the entire tone.' },
    { icon: 'ri-list-ordered', tip: 'Provide a rough chapter outline or key topics to get a well-structured, coherent eBook.' },
    { icon: 'ri-pencil-line', tip: 'Specify the writing style: conversational, authoritative, storytelling, or how-to guide.' },
    { icon: 'ri-price-tag-line', tip: 'Mention if it\'s a lead magnet, paid product, or course companion — it affects depth and length.' },
  ],
  'Presentation Creator': [
    { icon: 'ri-slideshow-line', tip: 'State the slide count and audience type (investors, executives, students) for the right format.' },
    { icon: 'ri-palette-line', tip: 'Mention your brand colors or preferred visual style for consistent design suggestions.' },
    { icon: 'ri-time-line', tip: 'Include the presentation duration — it helps calibrate how much content goes on each slide.' },
    { icon: 'ri-mic-line', tip: 'Ask for speaker notes alongside each slide to make delivery smooth and confident.' },
  ],
  'Website Builder': [
    { icon: 'ri-smartphone-line', tip: 'Always request mobile-responsive layouts — over 60% of web traffic is on mobile devices.' },
    { icon: 'ri-search-2-line', tip: 'Ask for SEO-optimized headings and meta descriptions to rank higher on Google.' },
    { icon: 'ri-dashboard-line', tip: 'Specify your tech stack preference (React, HTML/CSS, Next.js) for compatible code output.' },
    { icon: 'ri-palette-line', tip: 'Describe your brand personality (bold, minimal, playful) to optimize the layout.' },
  ],
  'AI Image Generator': [
    { icon: 'ri-camera-line', tip: 'Describe lighting conditions (golden hour, studio lighting, neon glow) for dramatic results.' },
    { icon: 'ri-layout-line', tip: 'Specify an art style (photorealistic, oil painting, anime, watercolor) for consistent output.' },
    { icon: 'ri-zoom-in-line', tip: 'Add detail descriptors like "ultra-detailed", "8K resolution", or "cinematic quality".' },
    { icon: 'ri-contrast-line', tip: 'Describe the background explicitly — a simple background makes subjects pop more.' },
  ],
  'Master Architect': [
    { icon: 'ri-ruler-line', tip: 'Always specify the total square footage and number of floors for accurate floor plans.' },
    { icon: 'ri-sun-line', tip: 'Mention climate and orientation (south-facing, tropical, cold climate) for smart design.' },
    { icon: 'ri-leaf-line', tip: 'Ask for sustainable features (solar panels, rainwater harvesting) if eco-design is a priority.' },
    { icon: 'ri-user-line', tip: 'Describe the occupants (family of 4, co-working team, hotel guests) to optimize the layout.' },
  ],
  'AI Video': [
    { icon: 'ri-time-line', tip: 'Specify the exact video duration (15s, 30s, 60s, 2min) to get properly paced content.' },
    { icon: 'ri-movie-line', tip: 'Describe the mood and style clearly: cinematic, documentary, energetic, minimalist, vintage, etc.' },
    { icon: 'ri-music-line', tip: 'Mention audio preferences upfront: background music, voiceover, sound effects, or silent.' },
    { icon: 'ri-crop-line', tip: 'Always specify aspect ratio based on platform: 16:9 (YouTube), 9:16 (TikTok/Reels), 1:1 (Instagram).' },
  ],
};

const GENERIC_TIPS = [
  { icon: 'ri-lightbulb-line', tip: 'Be specific: the more detail you provide, the more accurate and useful the AI output will be.' },
  { icon: 'ri-text-wrap', tip: 'Use clear, complete sentences — avoid abbreviations or shorthand that the AI might misinterpret.' },
  { icon: 'ri-refresh-line', tip: 'If the first result isn\'t perfect, refine your prompt and regenerate — iteration is key.' },
  { icon: 'ri-tools-line', tip: 'Select the right tool for your task — each AI tool is optimized for a specific type of output.' },
  { icon: 'ri-file-copy-line', tip: 'Use templates as a starting point, then customize them with your specific details and goals.' },
];

// ── Main Component ────────────────────────────────────────────────────────────
export default function HomePage() {
  const navigate = useNavigate();
  const { isAdmin, user: adminUser, logout: adminLogout } = useAdminAuth();
  const [billingCycle, setBillingCycle] = useState<'monthly' | 'annual'>('monthly');
  const [checkoutPlan, setCheckoutPlan] = useState<Plan | null>(null);
  const [showLogin, setShowLogin] = useState(false);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [showEmailCapture, setShowEmailCapture] = useState(false);
  const [wordsUsedTotal, setWordsUsedTotal] = useState(0);
  const [toolGens, setToolGens] = useState<Record<string, number>>({});
  const [emailCaptured, setEmailCaptured] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [visibleSections, setVisibleSections] = useState<Set<string>>(new Set());
  const [inputText, setInputText] = useState('');
  const [wordCount, setWordCount] = useState(0);
  const [charCount, setCharCount] = useState(0);
  const [selectedTool, setSelectedTool] = useState<typeof TOOLS[0] | null>(null);
  const [toolDropdownOpen, setToolDropdownOpen] = useState(false);
  const [navToolsOpen, setNavToolsOpen] = useState(false);
  const [navPricingOpen, setNavPricingOpen] = useState(false);
  const [pasteSuccess, setPasteSuccess] = useState(false);
  const [copySuccess, setCopySuccess] = useState(false);
  const [toasts, setToasts] = useState<Toast[]>([]);
  const [draftSaved, setDraftSaved] = useState(false);
  const [hasDraft, setHasDraft] = useState(false);
  const [draftRestored, setDraftRestored] = useState(false);
  const [placeholderText, setPlaceholderText] = useState('');
  const [placeholderIndex, setPlaceholderIndex] = useState(0);
  const [isTypingPlaceholder, setIsTypingPlaceholder] = useState(true);
  const [attachments, setAttachments] = useState<Attachment[]>([]);
  const [attachDropdownOpen, setAttachDropdownOpen] = useState(false);
  const [isDragOver, setIsDragOver] = useState(false);
  const [previewImage, setPreviewImage] = useState<Attachment | null>(null);
  const [darkMode, toggleDarkMode] = useDarkMode();
  const setDarkMode = (v: boolean | ((prev: boolean) => boolean)) => {
    const next = typeof v === 'function' ? v(darkMode) : v;
    if (next !== darkMode) toggleDarkMode();
  };

  // ── Countdown timer state ──────────────────────────────────────────────────
  const [countdown, setCountdown] = useState({ hours: 5, minutes: 47, seconds: 23 });

  // ── New state ──────────────────────────────────────────────────────────────
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [recentPrompts, setRecentPrompts] = useState<RecentPrompt[]>([]);
  const [showPromptsHistory, setShowPromptsHistory] = useState(false);
  const [showTemplates, setShowTemplates] = useState(false);
  const [templateSearch, setTemplateSearch] = useState('');
  const [favorites, setFavorites] = useState<FavoritePrompt[]>([]);
  const [showFavorites, setShowFavorites] = useState(false);
  const [showTips, setShowTips] = useState(false);
  const [toneInfo, setToneInfo] = useState<ReturnType<typeof analyzeTone>>({ tone: 'Neutral', icon: 'ri-equalizer-line', color: 'text-white/50', bgColor: 'bg-white/10', score: 0 });
  const [showFavLabelModal, setShowFavLabelModal] = useState(false);
  const [favLabelInput, setFavLabelInput] = useState('');

  const toastIdRef = useRef(0);
  const warnedAt90Ref = useRef(false);
  const warnedAt100Ref = useRef(false);
  const draftTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const placeholderTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const toneTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const imageInputRef = useRef<HTMLInputElement>(null);
  const videoInputRef = useRef<HTMLInputElement>(null);
  const attachDropdownRef = useRef<HTMLDivElement>(null);
  const promptsHistoryRef = useRef<HTMLDivElement>(null);
  const templatesRef = useRef<HTMLDivElement>(null);
  const favoritesRef = useRef<HTMLDivElement>(null);
  const dropZoneRef = useRef<HTMLDivElement>(null);
  const navToolsRef = useRef<HTMLDivElement>(null);
  const navPricingRef = useRef<HTMLDivElement>(null);
  const MAX_WORDS = 2500;

  // ── Load favorites & recent prompts on mount ──────────────────────────────
  useEffect(() => {
    try {
      const stored = localStorage.getItem(PROMPTS_KEY);
      if (stored) setRecentPrompts(JSON.parse(stored));
    } catch { /* ignore */ }
    try {
      const storedFavs = localStorage.getItem(FAVORITES_KEY);
      if (storedFavs) setFavorites(JSON.parse(storedFavs));
    } catch { /* ignore */ }
    try {
      const storedWords = localStorage.getItem(WORDS_USED_KEY);
      if (storedWords) setWordsUsedTotal(parseInt(storedWords, 10) || 0);
    } catch { /* ignore */ }
    try {
      const storedGens = localStorage.getItem(TOOL_GENS_KEY);
      if (storedGens) setToolGens(JSON.parse(storedGens));
    } catch { /* ignore */ }
    try {
      if (localStorage.getItem(EMAIL_CAPTURED_KEY)) setEmailCaptured(true);
    } catch { /* ignore */ }
  }, []);

  // ── Close dropdowns on outside click ─────────────────────────────────────
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (attachDropdownRef.current && !attachDropdownRef.current.contains(e.target as Node)) {
        setAttachDropdownOpen(false);
      }
      if (promptsHistoryRef.current && !promptsHistoryRef.current.contains(e.target as Node)) {
        setShowPromptsHistory(false);
      }
      if (favoritesRef.current && !favoritesRef.current.contains(e.target as Node)) {
        setShowFavorites(false);
      }
      if (navToolsRef.current && !navToolsRef.current.contains(e.target as Node)) {
        setNavToolsOpen(false);
      }
      if (navPricingRef.current && !navPricingRef.current.contains(e.target as Node)) {
        setNavPricingOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // ── Tone analysis (debounced) ─────────────────────────────────────────────
  useEffect(() => {
    if (toneTimerRef.current) clearTimeout(toneTimerRef.current);
    toneTimerRef.current = setTimeout(() => {
      setToneInfo(analyzeTone(inputText));
    }, 400);
    return () => { if (toneTimerRef.current) clearTimeout(toneTimerRef.current); };
  }, [inputText]);

  // ── Favorites helpers ─────────────────────────────────────────────────────
  const isCurrentTextFavorited = favorites.some(f => f.text === inputText.trim());

  const addFavorite = (label: string) => {
    if (!inputText.trim()) return;
    const entry: FavoritePrompt = {
      id: Math.random().toString(36).slice(2),
      text: inputText.trim(),
      toolName: selectedTool?.name ?? null,
      toolIcon: selectedTool?.icon ?? null,
      toolColor: selectedTool?.color ?? null,
      savedAt: Date.now(),
      label: label.trim() || inputText.trim().slice(0, 40) + (inputText.trim().length > 40 ? '…' : ''),
    };
    setFavorites(prev => {
      const updated = [entry, ...prev.filter(f => f.text !== inputText.trim())];
      try { localStorage.setItem(FAVORITES_KEY, JSON.stringify(updated)); } catch { /* ignore */ }
      return updated;
    });
    addToast('Added to Favorites! ⭐', 'success', 'ri-star-fill');
  };

  const removeFavorite = (id: string) => {
    setFavorites(prev => {
      const updated = prev.filter(f => f.id !== id);
      try { localStorage.setItem(FAVORITES_KEY, JSON.stringify(updated)); } catch { /* ignore */ }
      return updated;
    });
  };

  const loadFavorite = (fav: FavoritePrompt) => {
    setInputText(fav.text);
    const words = fav.text.trim().split(/\s+/);
    setWordCount(words.length);
    setCharCount(fav.text.length);
    if (fav.toolName) {
      const tool = TOOLS.find(t => t.name === fav.toolName);
      if (tool) setSelectedTool(tool);
    }
    setShowFavorites(false);
    addToast('Favorite prompt loaded!', 'success', 'ri-star-line');
  };

  const handleStarClick = () => {
    if (!inputText.trim()) {
      addToast('Type something first before starring it.', 'warning', 'ri-star-line');
      return;
    }
    if (isCurrentTextFavorited) {
      const fav = favorites.find(f => f.text === inputText.trim());
      if (fav) removeFavorite(fav.id);
      addToast('Removed from Favorites.', 'warning', 'ri-star-line');
      return;
    }
    setFavLabelInput('');
    setShowFavLabelModal(true);
  };

  // ── Load recent prompts on mount ──────────────────────────────────────────
  useEffect(() => {
    try {
      const stored = localStorage.getItem(PROMPTS_KEY);
      if (stored) setRecentPrompts(JSON.parse(stored));
    } catch {
      // ignore
    }
  }, []);

  // ── Close dropdowns on outside click ─────────────────────────────────────
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (attachDropdownRef.current && !attachDropdownRef.current.contains(e.target as Node)) {
        setAttachDropdownOpen(false);
      }
      if (promptsHistoryRef.current && !promptsHistoryRef.current.contains(e.target as Node)) {
        setShowPromptsHistory(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // ── Close templates panel on outside click ────────────────────────────────
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (attachDropdownRef.current && !attachDropdownRef.current.contains(e.target as Node)) {
        setAttachDropdownOpen(false);
      }
      if (promptsHistoryRef.current && !promptsHistoryRef.current.contains(e.target as Node)) {
        setShowPromptsHistory(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // ── Load template into editor ─────────────────────────────────────────────
  const loadTemplate = (prompt: string) => {
    const words = prompt.trim().split(/\s+/);
    const count = Math.min(words.length, MAX_WORDS);
    const trimmed = words.slice(0, MAX_WORDS).join(' ');
    setInputText(trimmed);
    setWordCount(count);
    setCharCount(trimmed.length);
    setShowTemplates(false);
    setTemplateSearch('');
    if (draftTimerRef.current) clearTimeout(draftTimerRef.current);
    draftTimerRef.current = setTimeout(() => saveDraft(trimmed, selectedTool?.name), 1200);
    addToast('Template loaded! Customize it and generate.', 'success', 'ri-layout-grid-line');
  };

  // ── Filtered templates ────────────────────────────────────────────────────
  const activeTemplates = selectedTool
    ? (TOOL_TEMPLATES[selectedTool.name] ?? GENERIC_TEMPLATES)
    : GENERIC_TEMPLATES;

  const filteredTemplates = templateSearch.trim()
    ? activeTemplates.filter(
        (t) =>
          t.label.toLowerCase().includes(templateSearch.toLowerCase()) ||
          t.prompt.toLowerCase().includes(templateSearch.toLowerCase())
      )
    : activeTemplates;

  const activeTips = selectedTool ? (TOOL_TIPS[selectedTool.name] ?? GENERIC_TIPS) : GENERIC_TIPS;

  // ── Ring progress ─────────────────────────────────────────────────────────
  const RING_SIZE = 32;
  const RING_STROKE = 3;
  const RING_RADIUS = (RING_SIZE - RING_STROKE) / 2;
  const RING_CIRCUMFERENCE = 2 * Math.PI * RING_RADIUS;
  const ringProgress = Math.min(wordCount / MAX_WORDS, 1);
  const ringDashOffset = RING_CIRCUMFERENCE * (1 - ringProgress);
  const ringColor =
    ringProgress >= 1
      ? '#f87171'
      : ringProgress >= 0.9
      ? '#fbbf24'
      : ringProgress >= 0.5
      ? '#34d399'
      : '#a78bfa';

  // ── Save a prompt to recent history ──────────────────────────────────────
  const saveToRecentPrompts = useCallback((text: string, tool: typeof TOOLS[0] | null) => {
    if (!text.trim()) return;
    const entry: RecentPrompt = {
      id: Math.random().toString(36).slice(2),
      text: text.trim(),
      toolName: tool?.name ?? null,
      toolIcon: tool?.icon ?? null,
      toolColor: tool?.color ?? null,
      savedAt: Date.now(),
    };
    setRecentPrompts((prev) => {
      const filtered = prev.filter((p) => p.text !== text.trim());
      const updated = [entry, ...filtered].slice(0, MAX_RECENT_PROMPTS);
      try { localStorage.setItem(PROMPTS_KEY, JSON.stringify(updated)); } catch { /* ignore */ }
      return updated;
    });
  }, []);

  const deleteRecentPrompt = (id: string) => {
    setRecentPrompts((prev) => {
      const updated = prev.filter((p) => p.id !== id);
      try { localStorage.setItem(PROMPTS_KEY, JSON.stringify(updated)); } catch { /* ignore */ }
      return updated;
    });
  };

  const clearAllRecentPrompts = () => {
    setRecentPrompts([]);
    localStorage.removeItem(PROMPTS_KEY);
    setShowPromptsHistory(false);
  };

  const loadRecentPrompt = (prompt: RecentPrompt) => {
    setInputText(prompt.text);
    const words = prompt.text.trim().split(/\s+/);
    setWordCount(words.length);
    setCharCount(prompt.text.length);
    if (prompt.toolName) {
      const tool = TOOLS.find((t) => t.name === prompt.toolName);
      if (tool) setSelectedTool(tool);
    }
    setShowPromptsHistory(false);
    addToast('Prompt loaded from history!', 'success', 'ri-history-line');
  };

  const formatTimeAgo = (ts: number) => {
    const diff = Date.now() - ts;
    const mins = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);
    if (mins < 1) return 'Just now';
    if (mins < 60) return `${mins}m ago`;
    if (hours < 24) return `${hours}h ago`;
    return `${days}d ago`;
  };

  // ── Attachment helpers ────────────────────────────────────────────────────
  const formatBytes = (bytes: number) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const getAttachmentType = (file: File): Attachment['type'] => {
    if (file.type.startsWith('image/')) return 'image';
    if (file.type.startsWith('video/')) return 'video';
    return 'file';
  };

  const processFiles = (files: FileList | null) => {
    if (!files || files.length === 0) return;
    const remaining = MAX_ATTACHMENTS - attachments.length;
    if (remaining <= 0) {
      addToast(`Maximum ${MAX_ATTACHMENTS} attachments allowed.`, 'warning', 'ri-attachment-line');
      return;
    }
    const toAdd = Array.from(files).slice(0, remaining);
    const oversized = toAdd.filter((f) => f.size > MAX_FILE_MB * 1024 * 1024);
    if (oversized.length > 0) {
      addToast(`Some files exceed the ${MAX_FILE_MB}MB limit and were skipped.`, 'warning', 'ri-alert-fill');
    }
    const valid = toAdd.filter((f) => f.size <= MAX_FILE_MB * 1024 * 1024);
    const newAttachments: Attachment[] = valid.map((file) => {
      const type = getAttachmentType(file);
      const att: Attachment = {
        id: Math.random().toString(36).slice(2),
        file,
        name: file.name,
        size: formatBytes(file.size),
        type,
      };
      if (type === 'image') {
        att.preview = URL.createObjectURL(file);
      }
      return att;
    });
    setAttachments((prev) => [...prev, ...newAttachments]);
    if (newAttachments.length > 0) {
      addToast(`${newAttachments.length} file${newAttachments.length > 1 ? 's' : ''} attached successfully.`, 'success', 'ri-check-line');
    }
  };

  const removeAttachment = (id: string) => {
    setAttachments((prev) => {
      const att = prev.find((a) => a.id === id);
      if (att?.preview) URL.revokeObjectURL(att.preview);
      if (previewImage?.id === id) setPreviewImage(null);
      return prev.filter((a) => a.id !== id);
    });
  };

  const handleAttachFiles = () => { setAttachDropdownOpen(false); fileInputRef.current?.click(); };
  const handleAttachImages = () => { setAttachDropdownOpen(false); imageInputRef.current?.click(); };
  const handleAttachVideos = () => { setAttachDropdownOpen(false); videoInputRef.current?.click(); };

  // ── Drag-and-drop handlers ────────────────────────────────────────────────
  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault(); e.stopPropagation(); setIsDragOver(true);
  };
  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault(); e.stopPropagation();
    if (dropZoneRef.current && !dropZoneRef.current.contains(e.relatedTarget as Node)) setIsDragOver(false);
  };
  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault(); e.stopPropagation(); setIsDragOver(false);
    if (e.dataTransfer.files?.length > 0) processFiles(e.dataTransfer.files);
  };

  // ── Toast helpers ──────────────────────────────────────────────────────────
  const addToast = useCallback((message: string, type: Toast['type'], icon: string) => {
    const id = ++toastIdRef.current;
    setToasts((prev) => [...prev.slice(-2), { id, message, type, icon }]);
    setTimeout(() => setToasts((prev) => prev.filter((t) => t.id !== id)), 4500);
  }, []);

  // ── Restore draft on mount ─────────────────────────────────────────────────
  useEffect(() => {
    const saved = localStorage.getItem(DRAFT_KEY);
    const savedTool = localStorage.getItem(DRAFT_TOOL_KEY);
    if (saved && saved.trim()) {
      setHasDraft(true);
      setInputText(saved);
      const words = saved.trim().split(/\s+/);
      setWordCount(words.length);
      setCharCount(saved.length);
      if (savedTool) {
        const tool = TOOLS.find((t) => t.name === savedTool);
        if (tool) setSelectedTool(tool);
      }
      setDraftRestored(true);
      setTimeout(() => setDraftRestored(false), 3500);
    }
  }, []);

  // ── Auto-save draft ────────────────────────────────────────────────────────
  const saveDraft = useCallback((text: string, toolName?: string) => {
    if (text.trim()) {
      localStorage.setItem(DRAFT_KEY, text);
      if (toolName) localStorage.setItem(DRAFT_TOOL_KEY, toolName);
      setHasDraft(true);
      setDraftSaved(true);
      setTimeout(() => setDraftSaved(false), 2000);
    } else {
      localStorage.removeItem(DRAFT_KEY);
      localStorage.removeItem(DRAFT_TOOL_KEY);
      setHasDraft(false);
    }
  }, []);

  const handleTextChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const value = e.target.value;
    const words = value.trim() === '' ? [] : value.trim().split(/\s+/);
    const count = words.length === 1 && words[0] === '' ? 0 : words.length;
    if (count <= MAX_WORDS) {
      setInputText(value);
      setWordCount(count);
      setCharCount(value.length);
      const ratio = count / MAX_WORDS;
      if (ratio >= 1 && !warnedAt100Ref.current) {
        warnedAt100Ref.current = true;
        addToast('Word limit reached! You\'ve hit the 2,500 word maximum.', 'error', 'ri-error-warning-fill');
      } else if (ratio >= 0.9 && !warnedAt90Ref.current) {
        warnedAt90Ref.current = true;
        addToast('Approaching limit — only ' + (MAX_WORDS - count) + ' words remaining.', 'warning', 'ri-alert-fill');
      } else if (ratio < 0.9) {
        warnedAt90Ref.current = false;
        warnedAt100Ref.current = false;
      }
      if (draftTimerRef.current) clearTimeout(draftTimerRef.current);
      draftTimerRef.current = setTimeout(() => saveDraft(value, selectedTool?.name), 1200);
    }
  };

  const handleClearDraft = () => {
    localStorage.removeItem(DRAFT_KEY);
    localStorage.removeItem(DRAFT_TOOL_KEY);
    setHasDraft(false);
    setInputText('');
    setWordCount(0);
    setCharCount(0);
    setAttachments([]);
    setPreviewImage(null);
    warnedAt90Ref.current = false;
    warnedAt100Ref.current = false;
  };

  const handleManualSave = () => {
    saveDraft(inputText, selectedTool?.name);
    addToast('Draft saved! Your text will be here when you return.', 'success', 'ri-save-3-fill');
  };

  // ── Cycling placeholder animation ─────────────────────────────────────────
  useEffect(() => {
    const prompts = selectedTool ? (TOOL_PROMPTS[selectedTool.name] ?? GENERIC_PROMPTS) : GENERIC_PROMPTS;
    let charIndex = 0;
    let currentPromptIndex = placeholderIndex % prompts.length;
    let typing = true;
    let cancelled = false;
    const tick = () => {
      if (cancelled) return;
      const target = prompts[currentPromptIndex];
      if (typing) {
        charIndex++;
        setPlaceholderText(target.slice(0, charIndex));
        setIsTypingPlaceholder(true);
        if (charIndex < target.length) {
          placeholderTimerRef.current = setTimeout(tick, 28);
        } else {
          placeholderTimerRef.current = setTimeout(() => { if (cancelled) return; typing = false; tick(); }, 2800);
        }
      } else {
        charIndex--;
        setPlaceholderText(target.slice(0, charIndex));
        setIsTypingPlaceholder(false);
        if (charIndex > 0) {
          placeholderTimerRef.current = setTimeout(tick, 14);
        } else {
          currentPromptIndex = (currentPromptIndex + 1) % prompts.length;
          setPlaceholderIndex(currentPromptIndex);
          typing = true;
          placeholderTimerRef.current = setTimeout(tick, 400);
        }
      }
    };
    setPlaceholderText('');
    charIndex = 0;
    placeholderTimerRef.current = setTimeout(tick, 600);
    return () => { cancelled = true; if (placeholderTimerRef.current) clearTimeout(placeholderTimerRef.current); };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTool]);

  const handlePasteFromClipboard = async () => {
    try {
      const text = await navigator.clipboard.readText();
      if (!text) return;
      const combined = inputText ? inputText + '\n' + text : text;
      const words = combined.trim().split(/\s+/);
      const trimmed = words.slice(0, MAX_WORDS).join(' ');
      setInputText(trimmed);
      setWordCount(Math.min(words.length, MAX_WORDS));
      setCharCount(trimmed.length);
      setPasteSuccess(true);
      setTimeout(() => setPasteSuccess(false), 2000);
      if (draftTimerRef.current) clearTimeout(draftTimerRef.current);
      draftTimerRef.current = setTimeout(() => saveDraft(trimmed, selectedTool?.name), 1200);
    } catch { /* clipboard access denied */ }
  };

  // ── Generate: open confirmation modal or upgrade gate ────────────────────
  const handleGenerate = () => {
    if (!inputText.trim() && attachments.length === 0) {
      addToast('Please enter some text or attach a file first.', 'warning', 'ri-edit-line');
      return;
    }
    // Admin bypasses ALL limits — no email capture, no upgrade prompt, no paywall ever
    if (isAdmin) { setShowConfirmModal(true); return; }
    // Check free word limit
    if (wordsUsedTotal >= FREE_WORD_LIMIT) {
      if (!emailCaptured) {
        setShowEmailCapture(true);
      } else {
        setShowUpgradeModal(true);
      }
      return;
    }
    setShowConfirmModal(true);
  };

  // ── Confirm and navigate ──────────────────────────────────────────────────
  const handleConfirmGenerate = () => {
    saveToRecentPrompts(inputText, selectedTool);
    // Track cumulative words used
    const newTotal = wordsUsedTotal + wordCount;
    setWordsUsedTotal(newTotal);
    try { localStorage.setItem(WORDS_USED_KEY, String(newTotal)); } catch { /* ignore */ }
    // Track first usage timestamp
    try {
      if (!localStorage.getItem(FIRST_USAGE_KEY)) {
        localStorage.setItem(FIRST_USAGE_KEY, String(Date.now()));
      }
    } catch { /* ignore */ }
    // Track tool generations
    if (selectedTool) {
      setToolGens(prev => {
        const updated = { ...prev, [selectedTool.name]: (prev[selectedTool.name] ?? 0) + 1 };
        try { localStorage.setItem(TOOL_GENS_KEY, JSON.stringify(updated)); } catch { /* ignore */ }
        return updated;
      });
    }
    setShowConfirmModal(false);
    if (selectedTool) {
      navigate(selectedTool.path);
    } else {
      openSignup();
    }
  };

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => { entries.forEach((entry) => { if (entry.isIntersecting) setVisibleSections((prev) => new Set([...prev, entry.target.id])); }); },
      { threshold: 0.1 }
    );
    document.querySelectorAll('[data-animate]').forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  const openCheckout = (plan: Plan) => setCheckoutPlan(plan);
  const openSignup = () => openCheckout(PLANS[1]);
  const animClass = (id: string) => visibleSections.has(id) ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8';
  const imageAttachments = attachments.filter((a) => a.type === 'image' && a.preview);

  // ── Countdown timer effect ─────────────────────────────────────────────────
  useEffect(() => {
    const timer = setInterval(() => {
      setCountdown(prev => {
        let { hours, minutes, seconds } = prev;
        seconds -= 1;
        if (seconds < 0) { seconds = 59; minutes -= 1; }
        if (minutes < 0) { minutes = 59; hours -= 1; }
        if (hours < 0) { hours = 5; minutes = 59; seconds = 59; }
        return { hours, minutes, seconds };
      });
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  // ── Copy to Clipboard ─────────────────────────────────────────────────────
  const handleCopyToClipboard = async () => {
    if (!inputText.trim()) {
      addToast('Nothing to copy — type something first.', 'warning', 'ri-clipboard-line');
      return;
    }
    try {
      await navigator.clipboard.writeText(inputText);
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 2000);
      addToast('Prompt copied to clipboard!', 'success', 'ri-clipboard-check-line');
    } catch {
      addToast('Could not access clipboard.', 'error', 'ri-error-warning-line');
    }
  };

  return (
    <div className={`min-h-screen font-sans transition-colors duration-300 ${darkMode ? 'bg-gray-950' : 'bg-white'}`}>

      {/* Hidden file inputs */}
      <input ref={fileInputRef} type="file" multiple accept=".pdf,.doc,.docx,.txt,.csv,.xlsx,.pptx,.zip" className="hidden" onChange={(e) => { processFiles(e.target.files); e.target.value = ''; }} />
      <input ref={imageInputRef} type="file" multiple accept="image/*" className="hidden" onChange={(e) => { processFiles(e.target.files); e.target.value = ''; }} />
      <input ref={videoInputRef} type="file" multiple accept="video/*" className="hidden" onChange={(e) => { processFiles(e.target.files); e.target.value = ''; }} />

      {/* ── FAVORITE LABEL MODAL ───────────────────────────────────────────── */}
      {showFavLabelModal && (
        <div className="fixed inset-0 z-[10001] flex items-center justify-center p-6 bg-black/60 backdrop-blur-sm" onClick={() => setShowFavLabelModal(false)}>
          <div className={`relative w-full max-w-sm rounded-3xl shadow-2xl overflow-hidden animate-modal-in ${darkMode ? 'bg-gray-900 border border-gray-700' : 'bg-white'}`} onClick={e => e.stopPropagation()}>
            <div className={`flex items-center justify-between px-6 py-5 border-b ${darkMode ? 'border-gray-700' : 'border-gray-100'}`}>
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 flex items-center justify-center rounded-xl bg-gradient-to-br from-amber-400 to-orange-400 flex-shrink-0">
                  <i className="ri-star-fill text-white text-base"></i>
                </div>
                <div>
                  <h3 className={`text-base font-extrabold ${darkMode ? 'text-white' : 'text-gray-900'}`}>Save to Favorites</h3>
                  <p className={`text-xs font-medium ${darkMode ? 'text-gray-400' : 'text-gray-400'}`}>Give this prompt a memorable name</p>
                </div>
              </div>
              <button onClick={() => setShowFavLabelModal(false)} className={`w-8 h-8 flex items-center justify-center rounded-xl transition-all cursor-pointer flex-shrink-0 ${darkMode ? 'bg-gray-800 hover:bg-gray-700 text-gray-400' : 'bg-gray-100 hover:bg-gray-200 text-gray-500'}`}>
                <i className="ri-close-line text-sm"></i>
              </button>
            </div>
            <div className="px-6 py-5 space-y-4">
              <div>
                <label className={`text-xs font-bold uppercase tracking-widest block mb-2 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Label (optional)</label>
                <input
                  type="text"
                  value={favLabelInput}
                  onChange={e => setFavLabelInput(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter') { addFavorite(favLabelInput); setShowFavLabelModal(false); } }}
                  placeholder="e.g. My best resume prompt…"
                  maxLength={60}
                  className={`w-full px-4 py-3 rounded-xl border text-sm placeholder-gray-400 outline-none focus:border-amber-400 focus:ring-2 focus:ring-amber-100 transition-all ${darkMode ? 'bg-gray-800 border-gray-600 text-white' : 'border-gray-200 text-gray-800'}`}
                  autoFocus
                />
                <p className={`text-xs mt-1.5 ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>Leave blank to auto-generate from your text</p>
              </div>
              <div className={`p-3 rounded-xl border ${darkMode ? 'bg-gray-800 border-gray-600' : 'bg-gray-50 border-gray-200'}`}>
                <p className={`text-xs line-clamp-2 leading-relaxed ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>{inputText.trim().slice(0, 120)}{inputText.trim().length > 120 ? '…' : ''}</p>
              </div>
            </div>
            <div className={`flex items-center gap-3 px-6 py-4 border-t ${darkMode ? 'border-gray-700 bg-gray-800/50' : 'border-gray-100 bg-gray-50/50'}`}>
              <button onClick={() => setShowFavLabelModal(false)} className={`flex-1 py-2.5 rounded-xl border text-sm font-semibold transition-all cursor-pointer whitespace-nowrap ${darkMode ? 'border-gray-600 text-gray-300 hover:bg-gray-700' : 'border-gray-200 text-gray-600 hover:bg-gray-100'}`}>
                Cancel
              </button>
              <button
                onClick={() => { addFavorite(favLabelInput); setShowFavLabelModal(false); }}
                className="flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl bg-gradient-to-r from-amber-400 to-orange-400 text-white text-sm font-bold hover:shadow-lg hover:scale-105 transition-all cursor-pointer whitespace-nowrap"
              >
                <i className="ri-star-fill text-sm"></i>
                Save Favorite
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── GENERATE CONFIRMATION MODAL ────────────────────────────────────── */}
      {showConfirmModal && (
        <div className="fixed inset-0 z-[10000] flex items-center justify-center p-6 bg-black/60 backdrop-blur-sm" onClick={() => setShowConfirmModal(false)}>
          <div
            className={`relative w-full max-w-lg rounded-3xl shadow-2xl overflow-hidden animate-modal-in ${darkMode ? 'bg-gray-900 border border-gray-700' : 'bg-white'}`}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal header */}
            <div className={`flex items-center justify-between px-6 py-5 border-b ${darkMode ? 'border-gray-700' : 'border-gray-100'}`}>
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 flex items-center justify-center rounded-xl bg-gradient-to-br from-violet-500 to-cyan-500 flex-shrink-0">
                  <i className="ri-sparkling-2-fill text-white text-base"></i>
                </div>
                <div>
                  <h3 className={`text-base font-extrabold ${darkMode ? 'text-white' : 'text-gray-900'}`}>Ready to Generate?</h3>
                  <p className={`text-xs font-medium ${darkMode ? 'text-gray-400' : 'text-gray-400'}`}>Review your request before sending</p>
                </div>
              </div>
              <button onClick={() => setShowConfirmModal(false)} className={`w-8 h-8 flex items-center justify-center rounded-xl transition-all cursor-pointer flex-shrink-0 ${darkMode ? 'bg-gray-800 hover:bg-gray-700 text-gray-400' : 'bg-gray-100 hover:bg-gray-200 text-gray-500'}`}>
                <i className="ri-close-line text-sm"></i>
              </button>
            </div>

            {/* Modal body */}
            <div className="px-6 py-5 space-y-4">
              <div className={`flex items-start gap-3 p-4 rounded-2xl border ${darkMode ? 'bg-gray-800 border-gray-700' : 'bg-gray-50 border-gray-100'}`}>
                <div className="w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <i className={`ri-tools-line text-sm ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}></i>
                </div>
                <div className="flex-1 min-w-0">
                  <p className={`text-xs font-bold uppercase tracking-widest mb-1.5 ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>AI Tool</p>
                  {selectedTool ? (
                    <div className="flex items-center gap-2.5">
                      <div className={`w-8 h-8 flex items-center justify-center rounded-lg bg-gradient-to-br ${selectedTool.color} flex-shrink-0`}>
                        <i className={`${selectedTool.icon} text-white text-sm`}></i>
                      </div>
                      <div>
                        <p className={`text-sm font-bold ${darkMode ? 'text-white' : 'text-gray-800'}`}>{selectedTool.name}</p>
                        <p className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-400'}`}>{selectedTool.tag}</p>
                      </div>
                    </div>
                  ) : (
                    <div className="flex items-center gap-2">
                      <div className={`w-8 h-8 flex items-center justify-center rounded-lg flex-shrink-0 ${darkMode ? 'bg-gray-700' : 'bg-gray-200'}`}>
                        <i className={`ri-apps-2-line text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}></i>
                      </div>
                      <p className={`text-sm italic ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>No tool selected — will open sign-up</p>
                    </div>
                  )}
                </div>
              </div>

              <div className={`flex items-start gap-3 p-4 rounded-2xl border ${darkMode ? 'bg-gray-800 border-gray-700' : 'bg-gray-50 border-gray-100'}`}>
                <div className="w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <i className={`ri-file-text-line text-sm ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}></i>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between mb-1.5">
                    <p className={`text-xs font-bold uppercase tracking-widest ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>Your Prompt</p>
                    <div className="flex items-center gap-2">
                      <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${wordCount / MAX_WORDS > 0.9 ? 'bg-rose-100 text-rose-600' : wordCount / MAX_WORDS > 0.7 ? 'bg-amber-100 text-amber-600' : 'bg-emerald-100 text-emerald-600'}`}>
                        {wordCount.toLocaleString()}<span className="text-white/35">/{MAX_WORDS.toLocaleString()} w</span>
                      </span>
                    </div>
                  </div>
                  {inputText.trim() ? (
                    <p className={`text-sm leading-relaxed line-clamp-4 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>{inputText.trim()}</p>
                  ) : (
                    <p className={`text-sm italic ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>No text entered — attachments only</p>
                  )}
                  <div className="mt-2.5 w-full h-1 rounded-full bg-gray-200 overflow-hidden">
                    <div
                      className={`h-full rounded-full transition-all duration-300 ${wordCount / MAX_WORDS > 0.9 ? 'bg-rose-400' : wordCount / MAX_WORDS > 0.7 ? 'bg-amber-400' : 'bg-gradient-to-r from-violet-400 to-cyan-400'}`}
                      style={{ width: `${Math.min((wordCount / MAX_WORDS) * 100, 100)}%` }}
                    />
                  </div>
                </div>
              </div>

              {attachments.length > 0 && (
                <div className={`flex items-start gap-3 p-4 rounded-2xl border ${darkMode ? 'bg-gray-800 border-gray-700' : 'bg-gray-50 border-gray-100'}`}>
                  <div className="w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <i className={`ri-attachment-2 text-sm ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}></i>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className={`text-xs font-bold uppercase tracking-widest mb-2 ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                      Attachments · {attachments.length}
                    </p>
                    <div className="space-y-1.5">
                      {attachments.slice(0, 5).map((att) => (
                        <div key={att.id} className="flex items-center gap-2.5">
                          {att.type === 'image' && att.preview ? (
                            <div className="w-7 h-7 flex items-center justify-center rounded-lg overflow-hidden flex-shrink-0 border border-gray-200">
                              <img src={att.preview} alt={att.name} className="w-full h-full object-cover" />
                            </div>
                          ) : (
                            <div className={`w-7 h-7 flex items-center justify-center rounded-lg flex-shrink-0 ${att.type === 'video' ? 'bg-cyan-100' : 'bg-violet-100'}`}>
                              <i className={`${att.type === 'video' ? 'ri-video-line text-cyan-600' : 'ri-file-text-line text-violet-600'} text-xs`}></i>
                            </div>
                          )}
                          <p className={`text-xs font-semibold text-gray-800 line-clamp-2 leading-relaxed group-hover:text-amber-600 transition-colors`}>{att.name}</p>
                          <span className={`text-xs flex-shrink-0 ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>{att.size}</span>
                        </div>
                      ))}
                      {attachments.length > 5 && (
                        <p className={`text-xs font-medium pl-9 ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>+{attachments.length - 5} more file{attachments.length - 5 > 1 ? 's' : ''}</p>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>

            <div className={`flex items-center justify-between px-6 py-4 border-t ${darkMode ? 'border-gray-700 bg-gray-800/50' : 'border-gray-100 bg-gray-50/50'}`}>
              <button
                onClick={() => setShowConfirmModal(false)}
                className={`flex items-center gap-1.5 px-4 py-2.5 rounded-xl text-sm font-semibold transition-all cursor-pointer whitespace-nowrap border ${darkMode ? 'bg-gray-800 border-gray-600 text-gray-300 hover:bg-gray-700' : 'bg-white/10 border-gray-200 text-gray-600 hover:bg-gray-200'}`}
              >
                <i className="ri-arrow-left-line text-sm"></i>
                Go Back &amp; Edit
              </button>
              <button
                onClick={handleConfirmGenerate}
                className="flex items-center gap-1.5 px-6 py-2.5 rounded-xl bg-gradient-to-r from-violet-600 to-cyan-500 text-white text-sm font-bold hover:shadow-lg hover:scale-105 transition-all cursor-pointer whitespace-nowrap"
              >
                <i className="ri-sparkling-line text-base"></i>
                {selectedTool ? `Generate with ${selectedTool.name}` : 'Generate'}
                <i className="ri-arrow-right-line text-sm"></i>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── TOAST STACK ────────────────────────────────────────────────────── */}
      <div className="fixed top-20 right-5 z-[9999] flex flex-col gap-2 pointer-events-none">
        {toasts.map((toast) => (
          <div key={toast.id} className={`flex items-center gap-3 px-4 py-3 rounded-2xl shadow-xl text-sm font-semibold pointer-events-auto animate-toast-in max-w-xs ${toast.type === 'error' ? 'bg-rose-600 text-white' : toast.type === 'warning' ? 'bg-amber-500 text-white' : 'bg-emerald-600 text-white'}`}>
            <div className="w-5 h-5 flex items-center justify-center flex-shrink-0"><i className={`${toast.icon} text-base`}></i></div>
            <span className="leading-snug">{toast.message}</span>
            <button onClick={() => setToasts((p) => p.filter((t) => t.id !== toast.id))} className="ml-auto w-5 h-5 flex items-center justify-center opacity-70 hover:opacity-100 cursor-pointer flex-shrink-0"><i className="ri-close-line text-sm"></i></button>
          </div>
        ))}
      </div>

      {/* ── DRAFT RESTORED BANNER ──────────────────────────────────────────── */}
      {draftRestored && (
        <div className="fixed top-20 left-1/2 -translate-x-1/2 z-[9998] flex items-center gap-2.5 px-5 py-3 rounded-2xl bg-violet-600 text-white text-sm font-semibold shadow-xl animate-toast-in whitespace-nowrap">
          <div className="w-5 h-5 flex items-center justify-center flex-shrink-0"><i className="ri-history-line text-base"></i></div>
          Draft restored — your previous text is back!
        </div>
      )}

      {/* ── IMAGE LIGHTBOX ─────────────────────────────────────────────────── */}
      {previewImage && (
        <div className="fixed inset-0 z-[9999] bg-black/80 backdrop-blur-sm flex items-center justify-center p-6" onClick={() => setPreviewImage(null)}>
          <div className="relative max-w-3xl w-full bg-white/10 border border-white/20 rounded-3xl overflow-hidden shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-5 py-3 border-b border-white/15">
              <div className="flex items-center gap-2">
                <div className="w-5 h-5 flex items-center justify-center"><i className="ri-image-line text-white/70 text-sm"></i></div>
                <span className="text-white text-sm font-semibold truncate max-w-xs">{previewImage.name}</span>
                <span className="text-white/40 text-xs">{previewImage.size}</span>
              </div>
              <div className="flex items-center gap-2">
                <button onClick={() => removeAttachment(previewImage.id)} className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-rose-500/20 text-rose-300 hover:bg-rose-500/40 transition-colors cursor-pointer" title="Remove">
                  <i className="ri-delete-bin-line text-sm"></i>
                </button>
                <button onClick={() => setPreviewImage(null)} className="w-7 h-7 flex items-center justify-center rounded-lg bg-white/10 hover:bg-white/20 text-white transition-all cursor-pointer"><i className="ri-close-line text-sm"></i></button>
              </div>
            </div>
            <div className="w-full flex items-center justify-center bg-black/30" style={{ minHeight: '360px', maxHeight: '60vh' }}>
              <img src={previewImage.preview} alt={previewImage.name} className="max-w-full max-h-full object-contain" style={{ maxHeight: '60vh' }} />
            </div>
            {imageAttachments.length > 1 && (
              <div className="flex items-center justify-center gap-2 px-5 py-3 border-t border-white/15">
                {imageAttachments.map((img) => (
                  <button key={img.id} onClick={() => setPreviewImage(img)} className={`w-10 h-10 rounded-lg overflow-hidden border-2 transition-all cursor-pointer flex-shrink-0 ${previewImage.id === img.id ? 'border-white/80 scale-110' : 'border-white/20 opacity-60 hover:opacity-100'}`}>
                    <img src={img.preview} alt={img.name} className="w-full h-full object-cover" />
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── NAVBAR ─────────────────────────────────────────────────────────── */}
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${scrolled ? (darkMode ? 'bg-gray-950/95 backdrop-blur-md shadow-sm border-b border-gray-800' : 'bg-white/95 backdrop-blur-md shadow-sm border-b border-gray-100') : 'bg-transparent'}`}>
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2 cursor-pointer" onClick={() => navigate('/')}>
            <img src="https://static.readdy.ai/image/0de80e9fd237f0b8df79ee5619c3a653/49cc0aa73e52e5f4616af3a61a585458.jpeg" alt="openclaw.ai Logo" className="h-12 w-auto object-contain" />
          </div>
          <div className="hidden md:flex items-center gap-7">
            {/* Home link */}
            <a
              href="#home"
              onClick={(e) => { e.preventDefault(); document.getElementById('home')?.scrollIntoView({ behavior: 'smooth' }); setNavPricingOpen(false); setNavToolsOpen(false); }}
              className={`text-sm font-semibold transition-colors hover:text-violet-500 ${scrolled ? (darkMode ? 'text-gray-300' : 'text-gray-600') : 'text-white/80'}`}
            >
              Home
            </a>

            {/* ── Pricing dropdown ── */}
            <div className="relative" ref={navPricingRef}>
              <button
                onClick={() => { setNavPricingOpen(v => !v); setNavToolsOpen(false); }}
                className={`flex items-center gap-1.5 text-sm font-semibold transition-colors hover:text-violet-500 cursor-pointer ${scrolled ? (darkMode ? 'text-gray-300' : 'text-gray-600') : 'text-white/80'}`}
              >
                Pricing
                <i className={`ri-arrow-down-s-line text-base transition-transform duration-200 ${navPricingOpen ? 'rotate-180' : ''}`}></i>
              </button>

              {navPricingOpen && (
                <div className={`absolute top-full left-1/2 -translate-x-1/2 mt-3 w-[760px] rounded-2xl border overflow-hidden z-[200] ${darkMode ? 'bg-gray-900 border-gray-700' : 'bg-white border-gray-100'}`} style={{ boxShadow: '0 20px 60px rgba(0,0,0,0.15)' }}>
                  {/* Header */}
                  <div className={`px-6 py-4 border-b flex items-center justify-between ${darkMode ? 'border-gray-700 bg-gray-800/60' : 'border-gray-100 bg-gray-50/70'}`}>
                    <div>
                      <p className={`text-sm font-extrabold ${darkMode ? 'text-white' : 'text-gray-900'}`}>Simple, Transparent Pricing</p>
                      <p className={`text-xs mt-0.5 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Start free, upgrade when you&apos;re ready. No hidden fees, cancel anytime.</p>
                    </div>
                    {/* Countdown + billing toggle */}
                    <div className="flex flex-col items-end gap-2 flex-shrink-0">
                      {/* Limited offer banner */}
                      <div className="flex items-center gap-2 px-3 py-1.5 rounded-xl bg-orange-50 border border-orange-200">
                        <i className="ri-fire-fill text-orange-500 text-xs animate-pulse"></i>
                        <span className="text-xs font-bold text-orange-700">50% off ends in:</span>
                        <div className="flex items-center gap-1">
                          <div className="flex items-center justify-center bg-orange-500 text-white rounded-md px-1.5 py-0.5 min-w-[28px]">
                            <span className="text-xs font-extrabold tabular-nums">{String(countdown.hours).padStart(2, '0')}</span>
                          </div>
                          <span className="text-orange-500 font-bold text-xs">:</span>
                          <div className="flex items-center justify-center bg-orange-500 text-white rounded-md px-1.5 py-0.5 min-w-[28px]">
                            <span className="text-xs font-extrabold tabular-nums">{String(countdown.minutes).padStart(2, '0')}</span>
                          </div>
                          <span className="text-orange-500 font-bold text-xs">:</span>
                          <div className="flex items-center justify-center bg-orange-500 text-white rounded-md px-1.5 py-0.5 min-w-[28px]">
                            <span className="text-xs font-extrabold tabular-nums">{String(countdown.seconds).padStart(2, '0')}</span>
                          </div>
                        </div>
                      </div>
                      {/* Billing toggle */}
                      <div className="inline-flex items-center gap-1 bg-gray-100 rounded-full px-1 py-1">
                        <button
                          onClick={() => setBillingCycle('monthly')}
                          className={`px-3 py-1 rounded-full text-xs font-bold transition-all cursor-pointer whitespace-nowrap ${billingCycle === 'monthly' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}
                        >
                          Monthly
                        </button>
                        <button
                          onClick={() => setBillingCycle('annual')}
                          className={`px-3 py-1 rounded-full text-xs font-bold transition-all cursor-pointer whitespace-nowrap flex items-center gap-1.5 ${billingCycle === 'annual' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}
                        >
                          Annual
                          <span className="text-xs font-bold text-emerald-600 bg-emerald-50 px-1.5 py-0.5 rounded-full">Save 50%</span>
                        </button>
                      </div>
                    </div>
                  </div>

                  {/* Plan cards */}
                  <div className="p-5 grid grid-cols-3 gap-4">
                    {PLANS.map((plan) => {
                      const isPro = plan.name === 'Pro';
                      const isProMax = plan.name === 'Golden Pro';
                      const displayPrice = billingCycle === 'annual' ? plan.annualPrice : plan.price;
                      const displayPeriod = billingCycle === 'annual' ? plan.annualPeriod : plan.period;
                      return (
                        <div
                          key={plan.name}
                          className={`relative rounded-2xl p-5 flex flex-col gap-3 ${isPro ? 'bg-gradient-to-b from-violet-600 to-cyan-600 text-white' : darkMode ? 'bg-gray-800 border border-gray-700' : 'bg-gray-50 border border-gray-100'}`}
                        >
                          {isPro && (
                            <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 bg-gradient-to-r from-amber-400 to-orange-400 rounded-full text-xs font-extrabold text-white shadow whitespace-nowrap">
                              ⭐ Most Popular
                            </div>
                          )}
                          {/* Plan header */}
                          <div>
                            <div className={`w-8 h-8 flex items-center justify-center rounded-xl mb-2 ${isPro ? 'bg-white/20' : isProMax ? 'bg-gray-200' : 'bg-emerald-50'}`}>
                              <i className={`${plan.name === 'Free Trial' ? 'ri-seedling-line text-emerald-600' : plan.name === 'Pro' ? 'ri-rocket-line text-white' : 'ri-building-2-line text-gray-700'} text-base`}></i>
                            </div>
                            <p className={`text-sm font-extrabold ${isPro ? 'text-white' : darkMode ? 'text-white' : 'text-gray-900'}`}>{plan.name}</p>
                            <div className="flex items-end gap-1 mt-0.5">
                              <span className={`text-2xl font-extrabold ${isPro ? 'text-white' : darkMode ? 'text-white' : 'text-gray-900'}`}>{displayPrice}</span>
                              <span className={`text-xs mb-1 ${isPro ? 'text-white/70' : 'text-emerald-600'}`}>{displayPeriod}</span>
                            </div>
                            {billingCycle === 'monthly' && (isPro || isProMax) && (
                              <div className="flex items-center gap-1 mt-1">
                                <i className="ri-time-line text-orange-400 text-xs"></i>
                                <span className="text-xs font-semibold text-orange-500 tabular-nums">
                                  {String(countdown.hours).padStart(2, '0')}:{String(countdown.minutes).padStart(2, '0')}:{String(countdown.seconds).padStart(2, '0')} left to save 50%
                                </span>
                              </div>
                            )}
                          </div>
                          {/* Features */}
                          <ul className="space-y-1.5 flex-1">
                            {plan.features.map((f) => (
                              <li key={f} className="flex items-start gap-2">
                                <div className="w-3.5 h-3.5 flex items-center justify-center flex-shrink-0 mt-0.5">
                                  <i className={`ri-check-line text-xs ${isPro ? 'text-cyan-200' : 'text-emerald-500'}`}></i>
                                </div>
                                <span className={`text-xs leading-snug ${isPro ? 'text-white/85' : darkMode ? 'text-gray-300' : 'text-gray-600'}`}>{f}</span>
                              </li>
                            ))}
                          </ul>
                          {/* CTA */}
                          <button
                            onClick={() => { setNavPricingOpen(false); openCheckout(plan); }}
                            className={`w-full py-2.5 rounded-xl text-xs font-bold transition-all cursor-pointer whitespace-nowrap mt-1 ${isPro ? 'bg-white text-violet-700 hover:bg-gray-50' : isProMax ? 'bg-gray-900 text-white hover:bg-gray-700' : 'bg-gradient-to-r from-emerald-500 to-teal-500 text-white hover:scale-[1.02]'}`}
                          >
                            {plan.name === 'Free Trial' ? 'Get Started Free' : `Get ${plan.name}`}
                          </button>
                        </div>
                      );
                    })}
                  </div>

                  {/* Footer */}
                  <div className={`px-6 py-3 border-t flex items-center justify-between ${darkMode ? 'border-gray-700 bg-gray-800/40' : 'border-gray-100 bg-gray-50/50'}`}>
                    <div className="flex items-center gap-2">
                      <i className="ri-shield-check-fill text-emerald-500 text-sm"></i>
                      <p className={`text-xs font-medium ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>30-day money-back guarantee · No questions asked · Cancel anytime</p>
                    </div>
                    <button
                      onClick={() => { setNavPricingOpen(false); document.getElementById('pricing')?.scrollIntoView({ behavior: 'smooth' }); }}
                      className="text-xs font-bold text-violet-600 hover:text-violet-700 transition-colors cursor-pointer flex items-center gap-1 whitespace-nowrap"
                    >
                      View Full Pricing <i className="ri-arrow-right-line text-xs"></i>
                    </button>
                  </div>
                </div>
              )}
            </div>

            {/* ── Tools dropdown ── */}
            <div className="relative" ref={navToolsRef}>
              <button
                onClick={() => { setNavToolsOpen(v => !v); setNavPricingOpen(false); }}
                className={`flex items-center gap-1.5 text-sm font-semibold transition-colors hover:text-violet-500 cursor-pointer ${scrolled ? (darkMode ? 'text-gray-300' : 'text-gray-600') : 'text-white/80'}`}
              >
                Tools
                <i className={`ri-arrow-down-s-line text-base transition-transform duration-200 ${navToolsOpen ? 'rotate-180' : ''}`}></i>
              </button>

              {navToolsOpen && (
                <div className={`absolute top-full left-1/2 -translate-x-1/2 mt-3 w-[680px] rounded-2xl border overflow-hidden z-[200] ${darkMode ? 'bg-gray-900 border-gray-700' : 'bg-white border-gray-100'}`} style={{ boxShadow: '0 20px 60px rgba(0,0,0,0.15)' }}>
                  {/* Header */}
                  <div className={`px-5 py-4 border-b ${darkMode ? 'border-gray-700 bg-gray-800/60' : 'border-gray-100 bg-gray-50/70'}`}>
                    <p className={`text-xs font-bold uppercase tracking-widest ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>AI Tools Suite — Powered by OpenClaw Algorithm</p>
                  </div>

                  {/* Tools grid */}
                  <div className="p-4 grid grid-cols-3 gap-2">
                    {TOOLS.map((tool) => (
                      <button
                        key={tool.name}
                        onClick={() => { setNavToolsOpen(false); navigate(tool.path); }}
                        className={`group flex items-center gap-3 p-3 rounded-xl text-left transition-all cursor-pointer ${darkMode ? 'hover:bg-gray-800' : 'hover:bg-gray-50'}`}
                      >
                        <div className={`w-9 h-9 flex items-center justify-center rounded-xl bg-gradient-to-br ${tool.color} flex-shrink-0 group-hover:scale-110 transition-transform`}>
                          <i className={`${tool.icon} text-white text-sm`}></i>
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-1.5">
                            <p className={`text-xs font-bold truncate ${darkMode ? 'text-white' : 'text-gray-900'}`}>{tool.name}</p>
                            {tool.badge === 'New' && (
                              <span className="text-xs font-bold px-1.5 py-0.5 rounded-full bg-gradient-to-r from-emerald-400 to-teal-500 text-white leading-none whitespace-nowrap">New</span>
                            )}
                            {tool.badge === 'Most Popular' && (
                              <span className="text-xs font-bold px-1.5 py-0.5 rounded-full bg-gradient-to-r from-violet-500 to-cyan-500 text-white leading-none whitespace-nowrap">🔥</span>
                            )}
                          </div>
                          <p className={`text-xs truncate mt-0.5 ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>{tool.tag}</p>
                        </div>
                      </button>
                    ))}
                  </div>

                  {/* Footer */}
                  <div className={`px-5 py-3 border-t flex items-center justify-between ${darkMode ? 'border-gray-700 bg-gray-800/40' : 'border-gray-100 bg-gray-50/50'}`}>
                    <p className={`text-xs font-medium ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>One subscription unlocks all {TOOLS.length} tools</p>
                    <button
                      onClick={() => { setNavToolsOpen(false); document.getElementById('pricing')?.scrollIntoView({ behavior: 'smooth' }); }}
                      className="text-xs font-bold text-violet-600 hover:text-violet-700 transition-colors cursor-pointer flex items-center gap-1 whitespace-nowrap"
                    >
                      View Pricing <i className="ri-arrow-right-line text-xs"></i>
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
          <div className="flex items-center gap-3">

            {/* ── Free Words Navbar Indicator — hidden for admin ── */}
            {!isAdmin && (() => {
              const remaining = Math.max(0, FREE_WORD_LIMIT - wordsUsedTotal);
              const usedPct = Math.min((wordsUsedTotal / FREE_WORD_LIMIT) * 100, 100);
              const isOver = wordsUsedTotal >= FREE_WORD_LIMIT;
              const isNear = !isOver && usedPct >= 70;

              const pillBg = isOver
                ? scrolled
                  ? (darkMode ? 'bg-rose-950/80 border-rose-700/60' : 'bg-rose-50 border-rose-200')
                  : 'bg-rose-500/25 border-rose-400/50'
                : isNear
                ? scrolled
                  ? (darkMode ? 'bg-amber-950/70 border-amber-700/50' : 'bg-amber-50 border-amber-200')
                  : 'bg-amber-500/20 border-amber-400/45'
                : scrolled
                  ? (darkMode ? 'bg-gray-800/80 border-gray-700/60' : 'bg-gray-50 border-gray-200')
                  : 'bg-white/15 border-white/30';

              const textColor = isOver
                ? scrolled ? (darkMode ? 'text-rose-400' : 'text-rose-600') : 'text-rose-200'
                : isNear
                ? scrolled ? (darkMode ? 'text-amber-400' : 'text-amber-600') : 'text-amber-200'
                : scrolled ? (darkMode ? 'text-gray-300' : 'text-gray-600') : 'text-white/80';

              const subColor = isOver
                ? scrolled ? (darkMode ? 'text-rose-500' : 'text-rose-400') : 'text-rose-300/80'
                : isNear
                ? scrolled ? (darkMode ? 'text-amber-500' : 'text-amber-500') : 'text-amber-300/80'
                : scrolled ? (darkMode ? 'text-gray-500' : 'text-gray-400') : 'text-white/45';

              const barColor = isOver ? 'bg-rose-500' : isNear ? 'bg-amber-400' : 'bg-emerald-400';
              const barTrack = scrolled
                ? (darkMode ? 'bg-gray-700' : 'bg-gray-200')
                : 'bg-white/20';

              return (
                <button
                  onClick={() => {
                    if (isOver) {
                      setShowUpgradeModal(true);
                    } else {
                      document.getElementById('home')?.scrollIntoView({ behavior: 'smooth' });
                    }
                  }}
                  title={isOver ? 'Free word limit reached — click to upgrade' : `${remaining} free words remaining`}
                  className={`hidden md:flex items-center gap-2.5 px-3.5 py-2 rounded-full border transition-all duration-300 cursor-pointer group ${pillBg}`}
                >
                  {/* Icon */}
                  <div className="w-4 h-4 flex items-center justify-center flex-shrink-0">
                    <i className={`text-xs transition-colors ${isOver ? 'ri-lock-2-fill' : isNear ? 'ri-error-warning-fill' : 'ri-file-word-line'} ${textColor}`}></i>
                  </div>

                  {/* Text + bar */}
                  <div className="flex flex-col items-start gap-0.5">
                    <span className={`text-xs font-bold leading-none whitespace-nowrap transition-colors ${textColor}`}>
                      {isOver ? 'Limit reached' : `${remaining.toLocaleString()} words left`}
                    </span>
                    {/* Mini progress bar */}
                    <div className={`w-16 h-1 rounded-full overflow-hidden transition-colors ${barTrack}`}>
                      <div
                        className={`h-full rounded-full transition-all duration-700 ${barColor} ${isOver ? 'animate-progress-pulse' : ''}`}
                        style={{ width: `${usedPct}%` }}
                      />
                    </div>
                  </div>

                  {/* Upgrade chevron when at limit */}
                  {isOver && (
                    <div className={`w-4 h-4 flex items-center justify-center flex-shrink-0 ${subColor}`}>
                      <i className="ri-arrow-right-s-line text-xs"></i>
                    </div>
                  )}
                </button>
              );
            })()}

            {/* ── Dark / Light Mode Toggle ── */}
            <button
              onClick={() => setDarkMode(v => !v)}
              title={darkMode ? 'Switch to Light Mode' : 'Switch to Dark Mode'}
              className={`w-9 h-9 flex items-center justify-center rounded-full transition-all cursor-pointer ${scrolled ? (darkMode ? 'bg-gray-800 text-amber-300 hover:bg-gray-700' : 'bg-gray-100 text-gray-600 hover:bg-gray-200') : 'bg-white/15 text-white/80 hover:bg-white/25'}`}
            >
              <i className={`text-base ${darkMode ? 'ri-sun-line' : 'ri-moon-line'}`}></i>
            </button>
            {isAdmin ? (
              <div className="flex items-center gap-2">
                <div className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-gradient-to-r from-amber-400/30 to-yellow-400/20 border border-amber-400/50">
                  <div className="w-5 h-5 flex items-center justify-center rounded-full bg-amber-400 flex-shrink-0">
                    <i className="ri-shield-star-fill text-white text-xs"></i>
                  </div>
                  <span className="text-xs font-extrabold text-amber-300 whitespace-nowrap">Admin · Golden Pro</span>
                </div>
                <button
                  onClick={() => { adminLogout(); addToast('Signed out successfully.', 'success', 'ri-logout-box-line'); }}
                  className={`text-xs font-semibold px-3 py-2 rounded-full transition-colors cursor-pointer whitespace-nowrap ${scrolled ? (darkMode ? 'text-gray-400 hover:bg-gray-800' : 'text-gray-500 hover:bg-gray-100') : 'text-white/60 hover:bg-white/10'}`}
                >
                  Sign Out
                </button>
              </div>
            ) : (
              <>
                <button onClick={() => setShowLogin(true)} className={`text-sm font-semibold px-4 py-2 rounded-full transition-colors hover:text-violet-500 ${scrolled ? (darkMode ? 'text-gray-300 hover:bg-gray-800' : 'text-gray-700 hover:bg-gray-100') : 'text-white/90 hover:bg-white/10'}`}>Sign In</button>
                <button onClick={openSignup} className="text-sm font-bold px-5 py-2 rounded-full bg-gradient-to-r from-violet-600 to-cyan-500 text-white hover:shadow-lg hover:scale-105 transition-all cursor-pointer whitespace-nowrap">Get Started Free</button>
              </>
            )}
          </div>
        </div>
      </nav>

      {/* ── HERO ───────────────────────────────────────────────────────────── */}
      <section id="home" className="relative min-h-screen flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0">
          <img src="https://public.readdy.ai/ai/img_res/8b5eeef27e68d2c87d85c83515872baf.jpg" alt="AI Platform Background" className="w-full h-full object-cover object-top" />
          <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/35 to-black/55"></div>
        </div>

        <div className="relative z-10 w-full flex items-center justify-center px-6 pt-24 pb-20">
          <div className="w-full max-w-3xl">

            {/* ── OpenClaw Algorithm Badge ── */}
            <div className="flex justify-center mb-6">
              <div className="inline-flex items-center gap-2.5 px-5 py-2.5 rounded-full bg-white/15 backdrop-blur-md border border-white/30 shadow-lg">
                <div className="w-6 h-6 flex items-center justify-center rounded-full bg-gradient-to-br from-violet-400 to-cyan-400 flex-shrink-0">
                  <i className="ri-cpu-line text-white text-xs"></i>
                </div>
                <span className="text-white text-sm font-bold">Powered by OpenClaw Algorithm</span>
                <div className="flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-400/20 border border-emerald-400/30">
                  <div className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse"></div>
                  <span className="text-emerald-300 text-xs font-bold">500B+ Tokens</span>
                </div>
              </div>
            </div>

            {/* ── Writing Box ── */}
            <div
              ref={dropZoneRef}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
              className={`relative bg-white/20 backdrop-blur-xl border rounded-3xl overflow-hidden shadow-2xl transition-all duration-200 ${isDragOver ? 'border-cyan-400/90 shadow-cyan-400/40 shadow-2xl scale-[1.01]' : 'border-white/40'}`}
            >
              {/* Drag-over overlay */}
              {isDragOver && (
                <div className="absolute inset-0 z-30 flex flex-col items-center justify-center bg-black/50 backdrop-blur-sm rounded-3xl pointer-events-none">
                  <div className="w-16 h-16 flex items-center justify-center rounded-2xl bg-cyan-400/20 border-2 border-cyan-400/60 mb-4 animate-bounce"><i className="ri-upload-cloud-2-line text-cyan-300 text-3xl"></i></div>
                  <p className="text-white text-lg font-bold">Drop files here</p>
                  <p className="text-white/60 text-sm mt-1">Images, videos, PDFs, documents…</p>
                </div>
              )}

              {/* Header — tool selector */}
              <div className="flex items-center gap-3 px-6 py-4 border-b border-white/25 bg-white/10">
                <img src="https://static.readdy.ai/image/0de80e9fd237f0b8df79ee5619c3a653/49cc0aa73e52e5f4616af3a61a585458.jpeg" alt="openclaw.ai Logo" className="h-10 w-auto object-contain" />
                {draftSaved && (
                  <span className="flex items-center gap-1 text-xs font-semibold text-emerald-300 animate-fade-in"><i className="ri-check-line text-sm"></i>Saved</span>
                )}
                {hasDraft && !draftSaved && inputText.trim() && (
                  <span className="flex items-center gap-1 text-white/50"><i className="ri-save-3-line text-xs"></i>Draft</span>
                )}
                <span className="hidden sm:flex items-center gap-1 text-xs text-white/50 font-medium ml-1"><i className="ri-drag-drop-line text-xs"></i>Drop files anywhere</span>

                {/* Tool selector dropdown */}
                <div className="ml-auto relative">
                  <button onClick={() => setToolDropdownOpen((v) => !v)} className="flex items-center gap-2 px-3.5 py-2 rounded-xl bg-white/20 hover:bg-white/30 border border-white/35 text-white text-xs font-semibold transition-all cursor-pointer whitespace-nowrap">
                    {selectedTool ? (<><i className={`${selectedTool.icon} text-sm`}></i><span>{selectedTool.name}</span></>) : (<><i className="ri-apps-2-line text-sm"></i><span>Select a Tool</span></>)}
                    <i className={`ri-arrow-down-s-line text-sm transition-transform duration-300 ${toolDropdownOpen ? 'rotate-180' : ''}`}></i>
                  </button>
                  {toolDropdownOpen && (
                    <div className="absolute right-0 top-full mt-2 w-64 bg-white/95 backdrop-blur-md border border-white/30 rounded-2xl shadow-2xl overflow-hidden z-50">
                      <button onClick={() => { setSelectedTool(null); setToolDropdownOpen(false); }} className="w-full flex items-center gap-2.5 px-4 py-3 text-xs font-semibold text-gray-400 hover:bg-gray-50 transition-colors cursor-pointer border-b border-gray-100"><i className="ri-close-circle-line text-sm"></i>No specific tool</button>
                      <div className="max-h-72 overflow-y-auto">
                        {TOOLS.map((tool) => (
                          <button key={tool.name} onClick={() => { setSelectedTool(tool); setToolDropdownOpen(false); }} className={`w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-gray-50 transition-colors cursor-pointer ${selectedTool?.name === tool.name ? 'bg-violet-50' : ''}`}>
                            <div className={`w-8 h-8 flex items-center justify-center rounded-lg flex-shrink-0 mt-0.5 transition-transform group-hover:scale-110 ${selectedTool ? `bg-gradient-to-br ${selectedTool.color}` : 'bg-gradient-to-br from-violet-500/70 to-cyan-500/70'}`}>
                              <i className={`${tool.icon} text-white text-sm`}></i>
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-white text-xs font-bold mb-1 group-hover:text-cyan-200 transition-colors">{tool.name}</p>
                              <p className="text-white/55 text-xs truncate group-hover:text-white/75 transition-colors">{tool.tag}</p>
                            </div>
                            {selectedTool?.name === tool.name && <i className="ri-check-line text-violet-600 text-sm flex-shrink-0"></i>}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Textarea */}
              <div className="relative bg-white/5">
                <textarea value={inputText} onChange={handleTextChange} onClick={() => setToolDropdownOpen(false)} placeholder="" className="w-full bg-transparent text-white text-sm leading-relaxed resize-none outline-none px-6 py-5 relative z-10" style={{ minHeight: '280px' }} />
                {!inputText && (
                  <div className="absolute top-5 left-6 right-6 pointer-events-none z-0">
                    <span className="text-white/50 text-sm leading-relaxed">
                      {placeholderText}
                      <span className={`inline-block w-0.5 h-4 bg-white/50 ml-0.5 align-middle ${isTypingPlaceholder ? 'animate-cursor-blink' : 'opacity-0'}`}></span>
                    </span>
                  </div>
                )}
              </div>

              {/* ── Word Count Progress Bar ── */}
              <div className="px-6 pb-3 pt-1">
                {(() => {
                  const pct = Math.min((wordCount / MAX_WORDS) * 100, 100);
                  const isRed = pct >= 90;
                  const isAmber = pct >= 70 && pct < 90;
                  const barColor = isRed
                    ? 'bg-gradient-to-r from-rose-500 to-red-500'
                    : isAmber
                    ? 'bg-gradient-to-r from-amber-400 to-orange-400'
                    : 'bg-gradient-to-r from-violet-500 to-cyan-400';
                  const labelColor = isRed ? 'text-rose-300' : isAmber ? 'text-amber-300' : 'text-white/50';
                  return (
                    <div>
                      <div className="flex items-center justify-between mb-1">
                        <span className={`text-xs font-semibold transition-colors duration-300 ${labelColor}`}>
                          {isRed
                            ? `⚠ ${MAX_WORDS - wordCount} words remaining`
                            : isAmber
                            ? `${MAX_WORDS - wordCount} words left`
                            : `${wordCount.toLocaleString()} / ${MAX_WORDS.toLocaleString()} words`}
                        </span>
                        <span className={`text-xs font-bold tabular-nums transition-colors duration-300 ${labelColor}`}>
                          {Math.round(pct)}%
                        </span>
                      </div>
                      <div className="w-full h-1.5 rounded-full bg-white/15 overflow-hidden">
                        <div
                          className={`h-full rounded-full transition-all duration-300 ${barColor} ${isRed ? 'animate-progress-pulse' : ''}`}
                          style={{ width: `${pct}%` }}
                        />
                      </div>
                    </div>
                  );
                })()}
              </div>

              {/* Image Preview Panel */}
              {imageAttachments.length > 0 && (
                <div className="px-6 pb-4">
                  <div className="flex items-center gap-2 mb-2">
                    <div className="w-4 h-4 flex items-center justify-center"><i className="ri-image-line text-white/60 text-xs"></i></div>
                    <span className="text-white/60 text-xs font-semibold uppercase tracking-widest">Image Previews · {imageAttachments.length}</span>
                  </div>
                  <div className="flex gap-3 flex-wrap">
                    {imageAttachments.map((img) => (
                      <div key={img.id} className="group relative cursor-pointer" onClick={() => setPreviewImage(img)}>
                        <div className="w-24 h-24 rounded-xl overflow-hidden border-2 border-white/30 hover:border-cyan-400/70 transition-all hover:scale-105 flex-shrink-0">
                          <img src={img.preview} alt={img.name} className="w-full h-full object-cover" />
                        </div>
                        <div className="absolute inset-0 rounded-xl bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center"><div className="w-6 h-6 flex items-center justify-center"><i className="ri-zoom-in-line text-white text-base"></i></div></div>
                        <button onClick={(e) => { e.stopPropagation(); removeAttachment(img.id); }} className="absolute -top-1.5 -right-1.5 w-4 h-4 flex items-center justify-center rounded-full bg-rose-500 text-white opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer"><i className="ri-close-line text-xs"></i></button>
                        <p className="text-white/60 text-xs mt-1 truncate w-24 text-center leading-tight">{img.name.length > 12 ? img.name.slice(0, 10) + '…' : img.name}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Non-image attachments */}
              {attachments.filter((a) => a.type !== 'image').length > 0 && (
                <div className="px-6 pb-3 flex flex-wrap gap-2">
                  {attachments.filter((a) => a.type !== 'image').map((att) => (
                    <div key={att.id} className="group relative flex items-center gap-2 bg-white/15 border border-white/30 rounded-xl px-3 py-2 max-w-[200px]">
                      {att.type === 'video' ? (
                        <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-white/20 flex-shrink-0"><i className="ri-video-line text-cyan-300 text-sm"></i></div>
                      ) : (
                        <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-white/20 flex-shrink-0"><i className="ri-file-text-line text-violet-300 text-sm"></i></div>
                      )}
                      <div className="flex-1 min-w-0">
                        <p className="text-white text-xs font-semibold truncate leading-relaxed">{att.name}</p>
                        <p className="text-white/50 text-xs">{att.size}</p>
                      </div>
                      <button onClick={() => removeAttachment(att.id)} className="absolute -top-1.5 -right-1.5 w-4 h-4 flex items-center justify-center rounded-full bg-rose-500 text-white opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer"><i className="ri-close-line text-xs"></i></button>
                    </div>
                  ))}
                </div>
              )}

              {/* Footer bar */}
              <div className="flex items-center justify-between px-6 py-4 border-t border-white/25 bg-white/10 gap-3 flex-wrap">
                {/* ── Ring + Word/Char Count + Tone Indicator ── */}
                <div className="flex items-center gap-3">
                  {/* SVG Ring */}
                  <div className="relative flex-shrink-0" style={{ width: RING_SIZE, height: RING_SIZE }}>
                    <svg width={RING_SIZE} height={RING_SIZE} className="-rotate-90">
                      <circle cx={RING_SIZE / 2} cy={RING_SIZE / 2} r={RING_RADIUS} fill="none" stroke="rgba(255,255,255,0.18)" strokeWidth={RING_STROKE} />
                      <circle cx={RING_SIZE / 2} cy={RING_SIZE / 2} r={RING_RADIUS} fill="none" stroke={ringColor} strokeWidth={RING_STROKE} strokeLinecap="round" strokeDasharray={RING_CIRCUMFERENCE} strokeDashoffset={ringDashOffset} style={{ transition: 'stroke-dashoffset 0.4s ease, stroke 0.4s ease' }} />
                    </svg>
                    {wordCount > 0 ? (
                      <div className="absolute inset-0 flex items-center justify-center">
                        <span className="font-bold leading-none" style={{ fontSize: '7px', color: ringColor, transition: 'color 0.4s ease' }}>{Math.round(ringProgress * 100)}%</span>
                      </div>
                    ) : (
                      <div className="absolute inset-0 flex items-center justify-center">
                        <i className="ri-edit-line text-white/40" style={{ fontSize: '9px' }}></i>
                      </div>
                    )}
                  </div>

                  {/* Text counts */}
                  <div className="flex items-center gap-2">
                    <span className={`text-xs font-semibold whitespace-nowrap ${wordCount / MAX_WORDS > 0.9 ? 'text-rose-300' : 'text-white/70'}`}>
                      {wordCount.toLocaleString()}<span className="text-white/40">/{MAX_WORDS.toLocaleString()} w</span>
                    </span>
                    <span className="text-white/30 text-xs">·</span>
                    <span className="text-xs font-semibold text-white/55 whitespace-nowrap">{charCount.toLocaleString()} <span className="text-white/35">chars</span></span>
                  </div>

                  {/* ── Tone Indicator ── */}
                  {wordCount >= 5 && (
                    <div className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full border border-white/20 transition-all duration-500 ${toneInfo.bgColor}`}>
                      <div className="w-3.5 h-3.5 flex items-center justify-center flex-shrink-0">
                        <i className={`${toneInfo.icon} text-xs ${toneInfo.color}`}></i>
                      </div>
                      <span className={`text-xs font-extrabold whitespace-nowrap ${toneInfo.color}`}>{toneInfo.tone}</span>
                    </div>
                  )}
                </div>

                {/* Action buttons */}
                <div className="flex items-center gap-2 flex-wrap">

                  {/* ── Star / Favorites button ── */}
                  <div className="relative" ref={favoritesRef}>
                    <button
                      onClick={() => {
                        if (inputText.trim()) {
                          handleStarClick();
                        } else {
                          setShowFavorites(v => !v);
                        }
                      }}
                      title={isCurrentTextFavorited ? 'Remove from favorites' : favorites.length > 0 && !inputText.trim() ? 'View favorites' : 'Save to favorites'}
                      className={`flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-semibold transition-all cursor-pointer whitespace-nowrap border ${isCurrentTextFavorited ? 'bg-amber-400/30 text-amber-300 border-amber-400/50' : favorites.length > 0 ? 'text-amber-300/80 hover:text-amber-300 hover:bg-white/15 border-amber-400/35' : 'text-white/40 border-transparent cursor-not-allowed opacity-50'}`}
                    >
                      <i className={`text-sm ${isCurrentTextFavorited ? 'ri-star-fill' : 'ri-star-line'}`}></i>
                      {favorites.length > 0 && (
                        <span className="flex items-center justify-center w-4 h-4 rounded-full bg-amber-400/30 text-amber-200 text-xs font-bold leading-none">{favorites.length}</span>
                      )}
                    </button>

                    {/* Favorites dropdown */}
                    {showFavorites && favorites.length > 0 && (
                      <div className="absolute bottom-full mb-2 right-0 w-80 bg-white/95 backdrop-blur-xl border border-white/30 rounded-2xl shadow-2xl overflow-hidden z-50">
                        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
                          <div className="flex items-center gap-2">
                            <div className="w-5 h-5 flex items-center justify-center"><i className="ri-star-fill text-amber-400 text-sm"></i></div>
                            <p className="text-xs font-bold text-gray-700 uppercase tracking-widest">Favorites</p>
                            <span className="text-xs font-bold text-emerald-600 bg-emerald-50 px-1.5 py-0.5 rounded-full">{favorites.length}</span>
                          </div>
                          <button onClick={() => { setFavorites([]); localStorage.removeItem(FAVORITES_KEY); setShowFavorites(false); }} className="text-xs font-semibold text-rose-400 hover:text-rose-600 transition-colors cursor-pointer whitespace-nowrap">Clear all</button>
                        </div>
                        <div className="max-h-72 overflow-y-auto">
                          {favorites.map((fav) => (
                            <div key={fav.id} className="group flex items-start gap-3 px-4 py-3 hover:bg-gray-50 transition-colors border-b border-gray-50 last:border-0">
                              {fav.toolIcon && fav.toolColor ? (
                                <div className={`w-7 h-7 flex items-center justify-center rounded-lg bg-gradient-to-br ${fav.toolColor} flex-shrink-0 mt-0.5`}>
                                  <i className={`${fav.toolIcon} text-white text-xs`}></i>
                                </div>
                              ) : (
                                <div className="w-7 h-7 flex items-center justify-center rounded-lg bg-amber-100 flex-shrink-0 mt-0.5">
                                  <i className="ri-star-fill text-amber-400 text-xs"></i>
                                </div>
                              )}
                              <div className="flex-1 min-w-0 cursor-pointer" onClick={() => loadFavorite(fav)}>
                                <p className="text-xs font-semibold text-gray-800 line-clamp-2 leading-relaxed group-hover:text-amber-600 transition-colors">{fav.label}</p>
                                <p className="text-xs text-gray-500 line-clamp-2 leading-relaxed mt-0.5">{fav.text}</p>
                                <div className="flex items-center gap-2 mt-1">
                                  {fav.toolName && <span className="text-xs text-gray-400 font-medium">{fav.toolName}</span>}
                                  {fav.toolName && <span className="text-gray-300 text-xs">·</span>}
                                  <span className="text-xs text-gray-400">{formatTimeAgo(fav.savedAt)}</span>
                                </div>
                              </div>
                              <div className="flex items-center gap-1 flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity">
                                <button onClick={() => loadFavorite(fav)} className="w-6 h-6 flex items-center justify-center rounded-lg bg-amber-100 text-amber-600 hover:bg-amber-200 transition-colors cursor-pointer" title="Load this prompt">
                                  <i className="ri-arrow-up-line text-xs"></i>
                                </button>
                                <button onClick={() => removeFavorite(fav.id)} className="w-6 h-6 flex items-center justify-center rounded-lg bg-rose-100 text-rose-500 hover:bg-rose-200 transition-colors cursor-pointer" title="Remove">
                                  <i className="ri-close-line text-xs"></i>
                                </button>
                              </div>
                            </div>
                          ))}
                        </div>
                        <div className="px-4 py-2.5 border-t border-gray-100 bg-gray-50/50">
                          <p className="text-xs text-gray-400 font-medium">Click any favorite to reload it into the editor</p>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Recent Prompts History button */}
                  <div className="relative" ref={promptsHistoryRef}>
                    <button
                      onClick={() => setShowPromptsHistory((v) => !v)}
                      className={`flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-semibold transition-all cursor-pointer whitespace-nowrap border ${recentPrompts.length > 0 ? 'bg-amber-400/25 text-amber-200 border-amber-400/50 hover:bg-amber-400/40' : 'text-white/40 border-transparent cursor-not-allowed opacity-50'}`}
                      disabled={recentPrompts.length === 0}
                      title="Recent prompts history"
                    >
                      <i className="ri-history-line text-sm"></i>
                      History
                      {recentPrompts.length > 0 && (
                        <span className="flex items-center justify-center w-4 h-4 rounded-full bg-amber-400/30 text-amber-200 text-xs font-bold leading-none">{recentPrompts.length}</span>
                      )}
                    </button>

                    {/* History dropdown panel */}
                    {showPromptsHistory && recentPrompts.length > 0 && (
                      <div className="absolute bottom-full mb-2 right-0 w-80 bg-white/95 backdrop-blur-xl border border-white/30 rounded-2xl shadow-2xl overflow-hidden z-50">
                        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
                          <div className="flex items-center gap-2">
                            <div className="w-5 h-5 flex items-center justify-center"><i className="ri-history-line text-amber-500 text-sm"></i></div>
                            <p className="text-xs font-bold text-gray-700 uppercase tracking-widest">Recent Prompts</p>
                            <span className="text-xs font-bold text-gray-400 bg-gray-100 px-1.5 py-0.5 rounded-full">{recentPrompts.length}</span>
                          </div>
                          <button onClick={clearAllRecentPrompts} className="text-xs font-semibold text-rose-400 hover:text-rose-600 transition-colors cursor-pointer whitespace-nowrap">Clear all</button>
                        </div>
                        <div className="max-h-72 overflow-y-auto">
                          {recentPrompts.map((prompt) => (
                            <div key={prompt.id} className="group flex items-start gap-3 px-4 py-3 hover:bg-gray-50 transition-colors border-b border-gray-50 last:border-0">
                              {prompt.toolIcon && prompt.toolColor ? (
                                <div className={`w-7 h-7 flex items-center justify-center rounded-lg bg-gradient-to-br ${prompt.toolColor} flex-shrink-0 mt-0.5`}>
                                  <i className={`${prompt.toolIcon} text-white text-xs`}></i>
                                </div>
                              ) : (
                                <div className="w-7 h-7 flex items-center justify-center rounded-lg bg-gray-100 flex-shrink-0 mt-0.5">
                                  <i className="ri-sparkling-line text-gray-400 text-xs"></i>
                                </div>
                              )}
                              <div className="flex-1 min-w-0 cursor-pointer" onClick={() => loadRecentPrompt(prompt)}>
                                <p className="text-xs font-semibold text-gray-800 line-clamp-2 leading-relaxed group-hover:text-violet-700 transition-colors">{prompt.text}</p>
                                <div className="flex items-center gap-2 mt-1">
                                  {prompt.toolName && <span className="text-xs text-gray-400 font-medium">{prompt.toolName}</span>}
                                  {prompt.toolName && <span className="text-gray-300 text-xs">·</span>}
                                  <span className="text-xs text-gray-400">{formatTimeAgo(prompt.savedAt)}</span>
                                </div>
                              </div>
                              <div className="flex items-center gap-1 flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity">
                                <button onClick={() => loadRecentPrompt(prompt)} className="w-6 h-6 flex items-center justify-center rounded-lg bg-violet-100 text-violet-600 hover:bg-violet-200 transition-colors cursor-pointer" title="Load this prompt">
                                  <i className="ri-arrow-up-line text-xs"></i>
                                </button>
                                <button onClick={() => deleteRecentPrompt(prompt.id)} className="w-6 h-6 flex items-center justify-center rounded-lg bg-rose-100 text-rose-500 hover:bg-rose-200 transition-colors cursor-pointer" title="Remove">
                                  <i className="ri-close-line text-xs"></i>
                                </button>
                              </div>
                            </div>
                          ))}
                        </div>
                        <div className="px-4 py-2.5 border-t border-gray-100 bg-gray-50/50">
                          <p className="text-xs text-gray-400 font-medium">Click any prompt to reload it into the editor</p>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Attach button with dropdown */}
                  <div className="relative" ref={attachDropdownRef}>
                    <button onClick={() => setAttachDropdownOpen((v) => !v)} className={`flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-semibold transition-all cursor-pointer whitespace-nowrap border ${attachments.length > 0 ? 'bg-violet-500/30 text-violet-200 border-violet-400/50 hover:bg-violet-500/40 hover:text-violet-100' : 'bg-cyan-500/20 text-cyan-200 border-cyan-400/40 hover:bg-cyan-500/35 hover:text-cyan-100'}`}>
                      <i className="ri-attachment-2 text-sm"></i>
                      {attachments.length > 0 ? `${attachments.length} file${attachments.length > 1 ? 's' : ''}` : 'Attach'}
                    </button>
                    {attachDropdownOpen && (
                      <div className="absolute bottom-full mb-2 left-0 w-52 bg-white/95 backdrop-blur-xl border border-white/30 rounded-2xl shadow-2xl overflow-hidden z-50">
                        <div className="px-3 py-2 border-b border-gray-100"><p className="text-xs font-bold text-gray-500 uppercase tracking-widest">Add Attachment</p></div>
                        <button onClick={handleAttachImages} className="w-full flex items-center gap-3 px-4 py-3 hover:bg-gray-50 transition-colors cursor-pointer">
                          <div className="w-7 h-7 flex items-center justify-center rounded-lg bg-gradient-to-br from-pink-500 to-rose-500 flex-shrink-0"><i className="ri-image-line text-white text-xs"></i></div>
                          <div className="text-left"><p className="text-xs font-bold text-gray-800">Pictures</p><p className="text-xs text-gray-400">JPG, PNG, GIF, WEBP</p></div>
                        </button>
                        <button onClick={handleAttachVideos} className="w-full flex items-center gap-3 px-4 py-3 hover:bg-gray-50 transition-colors cursor-pointer">
                          <div className="w-7 h-7 flex items-center justify-center rounded-lg bg-gradient-to-br from-cyan-500 to-teal-500 flex-shrink-0"><i className="ri-video-line text-white text-xs"></i></div>
                          <div className="text-left"><p className="text-xs font-bold text-gray-800">Videos</p><p className="text-xs text-gray-400">MP4, MOV, AVI, WEBM</p></div>
                        </button>
                        <button onClick={handleAttachFiles} className="w-full flex items-center gap-3 px-4 py-3 hover:bg-gray-50 transition-colors cursor-pointer">
                          <div className="w-7 h-7 flex items-center justify-center rounded-lg bg-gradient-to-br from-violet-500 to-indigo-500 flex-shrink-0"><i className="ri-file-text-line text-white text-xs"></i></div>
                          <div className="text-left"><p className="text-xs font-bold text-gray-800">Files</p><p className="text-xs text-gray-400">PDF, DOCX, TXT, CSV…</p></div>
                        </button>
                        <div className="px-4 py-2 border-t border-gray-100"><p className="text-xs text-gray-400">Max {MAX_FILE_MB}MB per file · Up to {MAX_ATTACHMENTS} files</p></div>
                      </div>
                    )}
                  </div>

                  {/* Paste */}
                  <button onClick={handlePasteFromClipboard} className={`flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-semibold transition-all cursor-pointer whitespace-nowrap border ${pasteSuccess ? 'bg-emerald-500/30 text-emerald-300 border-emerald-400/50' : 'bg-teal-500/20 text-teal-200 border-teal-400/40 hover:bg-teal-500/35 hover:text-teal-100'}`}>
                    <i className={`text-sm ${pasteSuccess ? 'ri-check-line' : 'ri-clipboard-line'}`}></i>
                    {pasteSuccess ? 'Pasted!' : 'Paste'}
                  </button>

                  {/* Save draft */}
                  <button onClick={handleManualSave} disabled={!inputText.trim()} className="flex items-center gap-1.5 px-3 py-2 rounded-xl bg-indigo-500/20 text-indigo-200 border border-indigo-400/40 hover:bg-indigo-500/35 hover:text-indigo-100 text-xs font-semibold transition-all cursor-pointer whitespace-nowrap disabled:opacity-30 disabled:cursor-not-allowed">
                    <i className="ri-save-3-line text-sm"></i>
                    Save Draft
                  </button>

                  {/* Clear */}
                  <button onClick={handleClearDraft} className="flex items-center gap-1.5 px-3 py-2 rounded-xl bg-rose-500/20 text-rose-300 border border-rose-400/35 hover:bg-rose-500/35 hover:text-rose-200 text-xs font-semibold transition-all cursor-pointer whitespace-nowrap">
                    <i className="ri-delete-bin-line text-sm"></i>
                    Clear
                  </button>

                  {/* ── Copy to Clipboard ── */}
                  <button
                    onClick={handleCopyToClipboard}
                    disabled={!inputText.trim()}
                    title="Copy prompt to clipboard"
                    className={`flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-semibold transition-all cursor-pointer whitespace-nowrap border disabled:opacity-30 disabled:cursor-not-allowed ${copySuccess ? 'bg-emerald-500/30 text-emerald-300 border-emerald-400/50' : 'bg-sky-500/20 text-sky-200 border-sky-400/40 hover:bg-sky-500/35 hover:text-sky-100'}`}
                  >
                    <i className={`text-sm ${copySuccess ? 'ri-check-double-line' : 'ri-clipboard-line'}`}></i>
                    {copySuccess ? 'Copied!' : 'Copy'}
                  </button>

                  {/* Words remaining indicator — hidden for admin */}
                  {!isAdmin && (() => {
                    const remaining = Math.max(0, FREE_WORD_LIMIT - wordsUsedTotal);
                    const usedPct = Math.min((wordsUsedTotal / FREE_WORD_LIMIT) * 100, 100);
                    const isOver = wordsUsedTotal >= FREE_WORD_LIMIT;
                    const isNear = !isOver && usedPct >= 70;
                    return (
                      <div className={`flex items-center gap-2 px-3 py-2 rounded-xl border transition-all ${
                        isOver
                          ? 'bg-rose-500/25 border-rose-400/50 text-rose-200'
                          : isNear
                          ? 'bg-amber-500/20 border-amber-400/45 text-amber-200'
                          : 'bg-white/12 border-white/25 text-white/60'
                      }`}>
                        <div className="w-3.5 h-3.5 flex items-center justify-center flex-shrink-0">
                          <i className={`text-xs ${isOver ? 'ri-lock-line' : 'ri-file-word-line'}`}></i>
                        </div>
                        <div className="flex flex-col gap-0.5">
                          <span className="text-xs font-bold leading-none whitespace-nowrap">
                            {isOver
                              ? 'Free limit reached'
                              : `${remaining.toLocaleString()} words left`}
                          </span>
                          <div className="w-20 h-1 rounded-full bg-white/20 overflow-hidden">
                            <div
                              className={`h-full rounded-full transition-all duration-500 ${
                                isOver ? 'bg-rose-400' : isNear ? 'bg-amber-400' : 'bg-emerald-400'
                              }`}
                              style={{ width: `${usedPct}%` }}
                            />
                          </div>
                        </div>
                      </div>
                    );
                  })()}

                  {/* Generate */}
                  <button
                    onClick={handleGenerate}
                    className={`flex items-center gap-2 px-5 py-2.5 rounded-xl text-white text-sm font-bold hover:shadow-lg hover:scale-105 transition-all cursor-pointer whitespace-nowrap ${
                      !isAdmin && wordsUsedTotal >= FREE_WORD_LIMIT
                        ? 'bg-gradient-to-r from-amber-500 to-orange-500'
                        : isAdmin
                        ? 'bg-gradient-to-r from-amber-500 to-yellow-400'
                        : 'bg-gradient-to-r from-violet-600 to-cyan-500'
                    }`}
                  >
                    {!isAdmin && wordsUsedTotal >= FREE_WORD_LIMIT ? (
                      <><i className="ri-vip-crown-line text-base"></i>Upgrade to Generate</>
                    ) : isAdmin ? (
                      <><i className="ri-shield-star-fill text-base"></i>{selectedTool ? `Generate with ${selectedTool.name}` : 'Generate'}</>
                    ) : (
                      <><i className="ri-sparkling-line text-base"></i>{selectedTool ? `Generate with ${selectedTool.name}` : 'Generate'}</>
                    )}
                  </button>
                </div>
              </div>
            </div>

            {/* ── TEMPLATES PANEL ──────────────────────────────────────────── */}
            <div ref={templatesRef} className="mt-4">
              {/* Toggle button */}
              <button
                onClick={() => setShowTemplates((v) => !v)}
                className={`w-full flex items-center justify-between px-5 py-3.5 rounded-2xl border transition-all duration-200 cursor-pointer group ${showTemplates ? 'bg-white/22 border-white/40 backdrop-blur-md' : 'bg-white/14 border-white/28 backdrop-blur-sm hover:bg-white/20 hover:border-white/38'}`}
              >
                <div className="flex items-center gap-3">
                  <div className="w-7 h-7 flex items-center justify-center rounded-lg bg-gradient-to-br from-violet-500/70 to-cyan-500/70 flex-shrink-0">
                    <i className="ri-layout-grid-line text-white text-sm"></i>
                  </div>
                  <div className="text-left">
                    <p className="text-white text-sm font-bold leading-tight">
                      Templates
                      {selectedTool && (
                        <span className="ml-2 text-xs font-semibold text-white/60">for {selectedTool.name}</span>
                      )}
                    </p>
                    <p className="text-white/55 text-xs font-medium">
                      {showTemplates ? 'Click a template to load it instantly' : `${activeTemplates.length} ready-to-use prompts — click to expand`}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-xs font-bold text-white/55 bg-white/15 px-2 py-0.5 rounded-full">{activeTemplates.length}</span>
                  <i className={`ri-arrow-down-s-line text-white/60 text-lg transition-transform duration-300 ${showTemplates ? 'rotate-180' : ''}`}></i>
                </div>
              </button>

              {/* Expanded panel */}
              {showTemplates && (
                <div className="mt-2 bg-white/16 backdrop-blur-md border border-white/30 rounded-2xl overflow-hidden animate-templates-in">
                  {/* Search bar */}
                  <div className="px-4 pt-4 pb-3 border-b border-white/15">
                    <div className="flex items-center gap-2.5 bg-white/15 border border-white/25 rounded-xl px-3 py-2">
                      <div className="w-4 h-4 flex items-center justify-center flex-shrink-0">
                        <i className="ri-search-line text-white/55 text-sm"></i>
                      </div>
                      <input
                        type="text"
                        value={templateSearch}
                        onChange={(e) => setTemplateSearch(e.target.value)}
                        placeholder="Search templates…"
                        className="flex-1 bg-transparent text-white text-xs font-medium placeholder-white/40 outline-none"
                      />
                      {templateSearch && (
                        <button onClick={() => setTemplateSearch('')} className="w-4 h-4 flex items-center justify-center cursor-pointer flex-shrink-0">
                          <i className="ri-close-line text-white/50 text-xs hover:text-white/80 transition-colors"></i>
                        </button>
                      )}
                    </div>
                  </div>

                  {/* Template grid */}
                  <div className="p-4">
                    {filteredTemplates.length === 0 ? (
                      <div className="flex flex-col items-center justify-center py-8 gap-2">
                        <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-white/15">
                          <i className="ri-search-line text-white/40 text-lg"></i>
                        </div>
                        <p className="text-white/50 text-sm font-medium">No templates match your search</p>
                        <button onClick={() => setTemplateSearch('')} className="text-xs text-violet-300 hover:text-violet-200 font-semibold cursor-pointer transition-colors">Clear search</button>
                      </div>
                    ) : (
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                        {filteredTemplates.map((tpl, idx) => (
                          <button
                            key={idx}
                            onClick={() => loadTemplate(tpl.prompt)}
                            className="group flex items-start gap-3 p-3.5 rounded-xl bg-white/12 hover:bg-white/22 border border-white/18 hover:border-white/38 text-left transition-all duration-150 cursor-pointer"
                          >
                            <div className={`w-8 h-8 flex items-center justify-center rounded-lg flex-shrink-0 mt-0.5 transition-transform group-hover:scale-110 ${selectedTool ? `bg-gradient-to-br ${selectedTool.color}` : 'bg-gradient-to-br from-violet-500/70 to-cyan-500/70'}`}>
                              <i className={`${tpl.icon} text-white text-sm`}></i>
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-white text-xs font-bold mb-1 group-hover:text-cyan-200 transition-colors">{tpl.label}</p>
                              <p className="text-white/55 text-xs leading-relaxed line-clamp-2 group-hover:text-white/75 transition-colors">{tpl.prompt}</p>
                            </div>
                            <div className="w-5 h-5 flex items-center justify-center flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity mt-0.5">
                              <i className="ri-corner-down-left-line text-cyan-300 text-xs"></i>
                            </div>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Footer hint */}
                  <div className={`px-4 py-3 border-t flex items-center justify-between ${darkMode ? 'border-gray-700 bg-gray-800/40' : 'border-gray-100 bg-gray-50/50'}`}>
                    <p className={`text-xs font-medium ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>Templates load into the editor — customize before generating</p>
                    {!selectedTool && (
                      <p className="text-xs text-gray-400 font-medium">
                        Select a tool above for tool-specific templates
                      </p>
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* ── TIPS PANEL ───────────────────────────────────────────────── */}
            <div className="mt-3">
              <button
                onClick={() => setShowTips(v => !v)}
                className={`w-full flex items-center justify-between px-5 py-3.5 rounded-2xl border transition-all duration-200 cursor-pointer group ${showTips ? 'bg-white/22 border-white/40 backdrop-blur-md' : 'bg-white/14 border-white/28 backdrop-blur-sm hover:bg-white/20 hover:border-white/38'}`}
              >
                <div className="flex items-center gap-3">
                  <div className="w-7 h-7 flex items-center justify-center rounded-lg bg-gradient-to-br from-emerald-500/70 to-teal-500/70 flex-shrink-0">
                    <i className="ri-lightbulb-flash-line text-white text-sm"></i>
                  </div>
                  <div className="text-left">
                    <p className="text-white text-sm font-bold leading-tight">
                      Writing Tips
                      {selectedTool && (
                        <span className="ml-2 text-xs font-semibold text-white/60">for {selectedTool.name}</span>
                      )}
                    </p>
                    <p className="text-white/55 text-xs font-medium">
                      {showTips ? 'Best-practice tips to get better AI results' : `${activeTips.length} expert tips — click to expand`}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-xs font-bold text-white/55 bg-white/15 px-2 py-0.5 rounded-full">{activeTips.length}</span>
                  <i className={`ri-arrow-down-s-line text-white/60 text-lg transition-transform duration-300 ${showTips ? 'rotate-180' : ''}`}></i>
                </div>
              </button>

              {showTips && (
                <div className="mt-2 bg-white/16 backdrop-blur-md border border-white/30 rounded-2xl overflow-hidden animate-templates-in">
                  <div className="p-4 grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {activeTips.map((tip, idx) => (
                      <div key={idx} className="flex items-start gap-3 p-3.5 rounded-xl bg-white/12 border border-white/18">
                        <div className="w-7 h-7 flex items-center justify-center rounded-lg bg-gradient-to-br from-emerald-500/60 to-teal-500/60 flex-shrink-0 mt-0.5">
                          <i className={`${tip.icon} text-emerald-200 text-sm`}></i>
                        </div>
                        <p className="text-white/80 text-xs leading-relaxed">{tip.tip}</p>
                      </div>
                    ))}
                  </div>
                  <div className="px-4 py-3 border-t border-white/15">
                    <p className="text-white/45 text-xs font-medium flex items-center gap-1.5">
                      <i className="ri-information-line text-xs"></i>
                      {selectedTool ? `Tips tailored for ${selectedTool.name} — select a different tool to see its tips` : 'Select a tool above for tool-specific writing tips'}
                    </p>
                  </div>
                </div>
              )}
            </div>

            {/* Quick tool chips */}
            <div className="flex flex-wrap items-center justify-center gap-2 mt-5">
              {['Paper Writer', 'Resume Builder', 'Business Plan', 'AI Image', 'Presentation', 'Genius Math'].map((chip) => (
                <button key={chip} onClick={() => { const tool = TOOLS.find((t) => t.name.toLowerCase().includes(chip.toLowerCase().split(' ')[0])); if (tool) { setSelectedTool(tool); setToolDropdownOpen(false); } }} className={`px-3.5 py-1.5 rounded-full backdrop-blur-sm border text-xs font-semibold transition-all cursor-pointer whitespace-nowrap ${selectedTool?.name.toLowerCase().includes(chip.toLowerCase().split(' ')[0]) ? 'bg-white/30 border-white/60 text-white' : 'bg-white/15 border-white/30 text-white/85 hover:bg-white/25 hover:text-white'}`}>
                  {chip}
                </button>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ── USAGE DASHBOARD ───────────────────────────────────────────────── */}
      <UsageDashboard
        darkMode={darkMode}
        wordsUsedExternal={wordsUsedTotal}
        toolGensExternal={toolGens}
      />

      {/* ── PRICING ────────────────────────────────────────────────────────── */}
      <section id="pricing" className="py-24" style={{ background: darkMode ? 'rgb(15,12,30)' : 'rgb(250,249,255)' }}>
        <div className="max-w-7xl mx-auto px-6">
          <div id="pricing-header" data-animate className={`text-center mb-12 transition-all duration-700 ${animClass('pricing-header')}`}>
            <span className="inline-block px-4 py-1.5 rounded-full bg-violet-50 text-violet-700 text-xs font-bold uppercase tracking-widest mb-4">Pricing</span>
            <h2 className="text-4xl font-extrabold text-gray-900 mb-4">Simple, Transparent Pricing</h2>
            <p className="text-gray-500 text-lg max-w-xl mx-auto mb-8">Start free, upgrade when you're ready. Powered by the OpenClaw Algorithm. No hidden fees, cancel anytime.</p>

            {/* ── Limited Offer Banner ── */}
            <div className="inline-flex items-center gap-3 mb-6 px-5 py-3 rounded-2xl bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-200 shadow-sm">
              <div className="w-6 h-6 flex items-center justify-center flex-shrink-0">
                <i className="ri-fire-fill text-orange-500 text-base animate-pulse"></i>
              </div>
              <span className="text-sm font-bold text-orange-700">Limited Offer — Annual plans at 50% off ends in:</span>
              <div className="flex items-center gap-1.5">
                <div className="flex flex-col items-center bg-orange-500 text-white rounded-lg px-2.5 py-1 min-w-[40px]">
                  <span className="text-base font-extrabold leading-none tabular-nums">{String(countdown.hours).padStart(2, '0')}</span>
                  <span className="text-xs font-semibold opacity-80 leading-none mt-0.5">hrs</span>
                </div>
                <span className="text-orange-500 font-extrabold text-lg">:</span>
                <div className="flex flex-col items-center bg-orange-500 text-white rounded-lg px-2.5 py-1 min-w-[40px]">
                  <span className="text-base font-extrabold leading-none tabular-nums">{String(countdown.minutes).padStart(2, '0')}</span>
                  <span className="text-xs font-semibold opacity-80 leading-none mt-0.5">min</span>
                </div>
                <span className="text-orange-500 font-extrabold text-lg">:</span>
                <div className="flex flex-col items-center bg-orange-500 text-white rounded-lg px-2.5 py-1 min-w-[40px]">
                  <span className="text-base font-extrabold leading-none tabular-nums">{String(countdown.seconds).padStart(2, '0')}</span>
                  <span className="text-xs font-semibold opacity-80 leading-none mt-0.5">sec</span>
                </div>
              </div>
            </div>

            <div className="flex flex-col items-center gap-3">
              <div className="inline-flex items-center gap-1 bg-gray-100 rounded-full px-1 py-1">
                <button onClick={() => setBillingCycle('monthly')} className={`px-3 py-1 rounded-full text-xs font-bold transition-all cursor-pointer whitespace-nowrap ${billingCycle === 'monthly' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}>Monthly</button>
                <button onClick={() => setBillingCycle('annual')} className={`px-3 py-1 rounded-full text-xs font-bold transition-all cursor-pointer whitespace-nowrap flex items-center gap-1.5 ${billingCycle === 'annual' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}>
                  Annual
                  <span className="text-xs font-bold text-emerald-600 bg-emerald-50 px-1.5 py-0.5 rounded-full">Save 50%</span>
                </button>
              </div>
              {billingCycle === 'monthly' && (
                <button
                  onClick={() => setBillingCycle('annual')}
                  className="flex items-center gap-1.5 text-xs font-semibold text-orange-600 hover:text-orange-700 transition-colors cursor-pointer animate-pulse"
                >
                  <i className="ri-arrow-up-line text-sm"></i>
                  Switch to Annual &amp; save 50% before the offer expires!
                </button>
              )}
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-5xl mx-auto">
            {PLANS.map((plan, i) => {
              const isPro = plan.name === 'Pro';
              const isProMax = plan.name === 'Golden Pro';
              const displayPrice = billingCycle === 'annual' ? plan.annualPrice : plan.price;
              const displayPeriod = billingCycle === 'annual' ? plan.annualPeriod : plan.period;
              return (
                <div key={plan.name} id={`plan-${i}`} data-animate className={`relative rounded-3xl p-8 transition-all duration-500 ${animClass(`plan-${i}`)} ${isPro ? 'bg-gradient-to-b from-violet-600 to-cyan-600 text-white shadow-2xl scale-105' : 'bg-white border border-gray-100 hover:shadow-xl'}`} style={{ transitionDelay: `${i * 100}ms` }}>
                  {isPro && <div className="absolute -top-4 left-1/2 -translate-x-1/2 px-4 py-1.5 bg-gradient-to-r from-amber-400 to-orange-400 rounded-full text-xs font-extrabold text-white shadow-lg whitespace-nowrap">⭐ Most Popular</div>}
                  <div className="mb-6">
                    <div className={`w-10 h-10 flex items-center justify-center rounded-xl mb-4 ${isPro ? 'bg-white/20' : isProMax ? 'bg-gray-100' : 'bg-emerald-50'}`}>
                      <i className={`${plan.name === 'Free Trial' ? 'ri-seedling-line text-emerald-600' : plan.name === 'Pro' ? 'ri-rocket-line text-white' : 'ri-building-2-line text-gray-700'} text-lg`}></i>
                    </div>
                    <h3 className={`text-xl font-extrabold mb-1 ${isPro ? 'text-white' : 'text-gray-900'}`}>{plan.name}</h3>
                    <div className="flex items-end gap-1">
                      <span className={`text-4xl font-extrabold ${isPro ? 'text-white' : 'text-gray-900'}`}>{displayPrice}</span>
                      <span className={`text-sm mb-1.5 ${isPro ? 'text-white/70' : 'text-emerald-600'}`}>{displayPeriod}</span>
                    </div>
                    {billingCycle === 'annual' && isPro && (
                      <div className="mt-2 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white/20 border border-white/30">
                        <i className="ri-price-tag-3-fill text-amber-300 text-xs"></i>
                        <span className="text-xs font-extrabold text-white">Save $240/year</span>
                      </div>
                    )}
                    {billingCycle === 'annual' && isProMax && (
                      <p className="text-xs font-semibold mt-1 text-emerald-600">Save $120/year</p>
                    )}
                    {billingCycle === 'monthly' && (isPro || isProMax) && (
                      <div className="mt-2 flex items-center gap-1.5">
                        <i className="ri-time-line text-orange-400 text-xs"></i>
                        <span className="text-xs font-semibold text-orange-500">
                          {String(countdown.hours).padStart(2, '0')}:{String(countdown.minutes).padStart(2, '0')}:{String(countdown.seconds).padStart(2, '0')} left to save 50%
                        </span>
                      </div>
                    )}
                  </div>
                  <ul className="space-y-3 mb-8">
                    {plan.features.map((f) => (
                      <li key={f} className="flex items-start gap-2.5">
                        <div className="w-4 h-4 flex items-center justify-center flex-shrink-0 mt-0.5"><i className={`ri-check-line text-sm ${isPro ? 'text-cyan-200' : 'text-emerald-500'}`}></i></div>
                        <span className={`text-sm ${isPro ? 'text-white/85' : 'text-gray-600'}`}>{f}</span>
                      </li>
                    ))}
                  </ul>
                  <button onClick={() => openCheckout(plan)} className={`w-full py-3.5 rounded-full text-sm font-bold transition-all cursor-pointer whitespace-nowrap ${isPro ? 'bg-white text-violet-700 hover:bg-gray-50 hover:shadow-lg' : isProMax ? 'bg-gray-900 text-white hover:bg-gray-700 hover:shadow-lg' : 'bg-gradient-to-r from-emerald-500 to-teal-500 text-white hover:shadow-lg hover:scale-[1.02]'}`}>
                    {plan.name === 'Free Trial' ? 'Get Started Free' : `Get ${plan.name}`}
                  </button>
                </div>
              );
            })}
          </div>
          <div className="text-center mt-10">
            <div className="inline-flex items-center gap-2 text-sm text-gray-500">
              <i className="ri-shield-check-fill text-emerald-500 text-base"></i>
              <span>30-day money-back guarantee · No questions asked · Cancel anytime</span>
            </div>
          </div>
        </div>
      </section>

      {/* ── FOOTER ─────────────────────────────────────────────────────────── */}
      <footer style={{ background: darkMode ? 'rgb(10,8,20)' : 'rgb(245,244,252)' }} className="pt-16 pb-8">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-10 mb-12">
            <div className="md:col-span-1">
              <div className="flex items-center gap-2 mb-4">
                <img src="https://static.readdy.ai/image/0de80e9fd237f0b8df79ee5619c3a653/49cc0aa73e52e5f4616af3a61a585458.jpeg" alt="openclaw.ai Logo" className="h-12 w-auto object-contain" />
              </div>
              <p className={`text-sm leading-relaxed mb-5 ${darkMode ? 'text-gray-500' : 'text-gray-500'}`}>The all-in-one AI creation platform powered by the OpenClaw Algorithm for professionals, students, and entrepreneurs.</p>
              <div className="flex items-center gap-3">
                {['ri-twitter-x-line', 'ri-linkedin-box-line', 'ri-instagram-line', 'ri-youtube-line'].map((icon) => (
                  <a key={icon} href="#" rel="nofollow" className={`w-8 h-8 flex items-center justify-center rounded-lg transition-all cursor-pointer ${darkMode ? 'bg-gray-800 border-gray-700 text-gray-400 hover:text-violet-400 hover:border-violet-700' : 'bg-white border-gray-200 text-gray-500 hover:text-violet-600 hover:border-violet-200'}`}><i className={`${icon} text-sm`}></i></a>
                ))}
              </div>
            </div>
            <div>
              <h4 className={`text-sm font-bold mb-4 uppercase tracking-widest ${darkMode ? 'text-gray-300' : 'text-gray-900'}`}><a href="#tools">AI Tools</a></h4>
              <ul className="space-y-2.5">
                {TOOLS.slice(0, 5).map((t) => (
                  <li key={t.name}><a href={t.path} onClick={(e) => { e.preventDefault(); navigate(t.path); }} className={`text-sm transition-colors cursor-pointer hover:text-violet-500 ${darkMode ? 'text-gray-500' : 'text-gray-500'}`}>{t.name}</a></li>
                ))}
              </ul>
            </div>
            <div>
              <h4 className={`text-sm font-bold mb-4 uppercase tracking-widest ${darkMode ? 'text-gray-300' : 'text-gray-900'}`}>More Tools</h4>
              <ul className="space-y-2.5">
                {TOOLS.slice(5).map((t) => (
                  <li key={t.name}><a href={t.path} onClick={(e) => { e.preventDefault(); navigate(t.path); }} className={`text-sm transition-colors cursor-pointer hover:text-violet-500 ${darkMode ? 'text-gray-500' : 'text-gray-500'}`}>{t.name}</a></li>
                ))}
              </ul>
            </div>
            <div>
              <h4 className={`text-sm font-bold mb-4 uppercase tracking-widest ${darkMode ? 'text-gray-300' : 'text-gray-900'}`}>Company</h4>
              <ul className="space-y-2.5">
                {['About Us', 'Blog', 'Careers', 'Press Kit', 'Contact', 'Privacy Policy', 'Terms of Service'].map((item) => (
                  <li key={item}><a href="#" rel="nofollow" className={`text-sm transition-colors cursor-pointer hover:text-violet-500 ${darkMode ? 'text-gray-500' : 'text-gray-500'}`}>{item}</a></li>
                ))}
              </ul>
            </div>
          </div>
          <div className={`border-t pt-8 flex flex-col md:flex-row items-center justify-between gap-4 ${darkMode ? 'border-gray-800' : 'border-gray-200'}`}>
            <p className={`text-xs ${darkMode ? 'text-gray-600' : 'text-gray-400'}`}>© {new Date().getFullYear()} openclaw.ai. All rights reserved.</p>
            <div className="flex items-center gap-2 text-xs text-gray-400">
              <i className="ri-shield-check-fill text-emerald-500 text-sm"></i>
              <span>SOC 2 Compliant · GDPR Ready · 256-bit SSL</span>
            </div>
          </div>
        </div>
      </footer>

      {/* ── MODALS ─────────────────────────────────────────────────────────── */}
      {checkoutPlan && <CheckoutModal plan={checkoutPlan} billingCycle={billingCycle} onClose={() => setCheckoutPlan(null)} />}
      {showLogin && <LoginModal onClose={() => setShowLogin(false)} onSwitchToSignup={() => { setShowLogin(false); openSignup(); }} onAdminLogin={() => addToast('Welcome back, Admin! Golden Pro access active.', 'success', 'ri-vip-crown-fill')} />}
      {showEmailCapture && (
        <EmailCaptureModal
          wordsUsed={wordsUsedTotal}
          freeLimit={FREE_WORD_LIMIT}
          onContinue={(email) => {
            setShowEmailCapture(false);
            if (email) {
              try { localStorage.setItem(EMAIL_CAPTURED_KEY, '1'); } catch { /* ignore */ }
              setEmailCaptured(true);
            }
            setShowUpgradeModal(true);
          }}
          onClose={() => setShowEmailCapture(false)}
        />
      )}
      {showUpgradeModal && (
        <UpgradePromptModal
          wordsUsed={wordsUsedTotal}
          freeLimit={FREE_WORD_LIMIT}
          onUpgrade={(plan) => { setShowUpgradeModal(false); openCheckout(plan); }}
          onDismiss={() => setShowUpgradeModal(false)}
        />
      )}

      <style>{`
        @keyframes fade-in {
          from { opacity: 0; transform: translateY(16px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        .animate-fade-in { animation: fade-in 0.7s ease forwards; }

        @keyframes toast-in {
          from { opacity: 0; transform: translateX(24px) scale(0.95); }
          to   { opacity: 1; transform: translateX(0) scale(1); }
        }
        .animate-toast-in { animation: toast-in 0.3s cubic-bezier(0.34,1.56,0.64,1) forwards; }

        @keyframes cursor-blink {
          0%, 100% { opacity: 1; }
          50%       { opacity: 0; }
        }
        .animate-cursor-blink { animation: cursor-blink 0.9s step-end infinite; }

        @keyframes modal-in {
          from { opacity: 0; transform: scale(0.95) translateY(12px); }
          to   { opacity: 1; transform: scale(1) translateY(0); }
        }
        .animate-modal-in { animation: modal-in 0.25s cubic-bezier(0.34,1.56,0.64,1) forwards; }

        @keyframes templates-in {
          from { opacity: 0; transform: translateY(-8px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        .animate-templates-in { animation: templates-in 0.22s ease forwards; }

        @keyframes progress-pulse {
          0%, 100% { opacity: 1; }
          50%       { opacity: 0.7; }
        }
        .animate-progress-pulse { animation: progress-pulse 1.2s ease-in-out infinite; }

        @keyframes new-badge-pulse {
          0%, 100% { box-shadow: 0 0 0 0 rgba(52,211,153,0.5); }
          50%       { box-shadow: 0 0 0 6px rgba(52,211,153,0); }
        }
        .animate-new-badge { animation: new-badge-pulse 2s ease-in-out infinite; }

        .line-clamp-2 {
          display: -webkit-box;
          -webkit-line-clamp: 2;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }
        .line-clamp-4 {
          display: -webkit-box;
          -webkit-line-clamp: 4;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }
      `}</style>
    </div>
  );
}
